import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useScroll, useTransform, type Variants } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import * as z from "zod";
import {
  Check,
  Menu,
  X,
  Phone,
  Mail,
  Award,
  CheckCircle2,
  ShieldCheck,
  UserCheck,
  Stethoscope,
  ClipboardCheck,
  PenSquare,
  Plane,
  Sparkles,
  Activity,
  CalendarClock,
  Star,
  Globe2,
  Users,
  ArrowRight,
  Ruler,
  Zap,
  Maximize2,
  Compass,
  HelpCircle,
  Heart,
  Smile,
  Scan,
  Trophy,
  Hotel,
  Car,
  Scissors,
  Building2,
  MapPin,
  Lock,
  Microscope,
  TrendingUp,
  Play,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { FaWhatsapp } from "react-icons/fa";
import { SiTrustpilot } from "react-icons/si";
import { FcGoogle } from "react-icons/fc";
import { MdVerified } from "react-icons/md";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import lightLogo from "@assets/rememore_light.png";
import jciCertificate from "@assets/jci_certificate.png";
import procFacial from "@assets/facial_1777877368345.jpeg";
import procBody from "@assets/body_1777877368345.jpeg";
import procBreast from "@assets/breast_1777877368345.jpeg";
import procHair from "@assets/hair_1777877368345.jpeg";
import procDental from "@assets/dental_1777877368345.jpeg";
import procBariatric from "@assets/bariatric_1777877368344.jpeg";
import hospBahcelievler from "@assets/bahcelievler_1777383982614.webp";
import hospSisli from "@assets/sisli-web_555x376px-1jpg_e49e_kopyası_1777385221519.png";
import hospBodrum from "@assets/bodrum-hastanesijpg_b79a_1777383982614.webp";
import hospGoztepe from "@assets/goztepe-web_555x376pxjpg_db7d_1777383982614.webp";
import hospAtasehir from "@assets/atasehir-web_555x376pxjpg_6731_1777385172890.webp";
import hospAnkara from "@assets/ankara-web_555x376px-1jpg_9c70_1777383982613.webp";
import sixpackBg from "@/assets/sixpack-bg.png";
import luxuryVipBg from "@/assets/luxury-vip-bg.png";
import whyusVipBg from "@/assets/whyus-vip-bg.png";
import patientMR from "@/assets/patient_mr.png";
import patientPA from "@/assets/patient_pa.png";
import patientGV from "@/assets/patient_gv.png";
import patientFS from "@/assets/patient_fs.png";
import patientVP from "@/assets/patient_vp.png";
import patientMan1 from "@assets/patient_man_1.png";
import patientMan2 from "@assets/patient_man_2.png";
import patientMan3 from "@assets/patient_man_3.png";
import testimonialUk from "@assets/79_1777550466591.jpg";
import testimonialAu from "@assets/39_1777550466592.jpg";
import testimonialDe from "@assets/65_1777550466592.jpg";
import whyUsCover from "@assets/image_1777550625528.png";
import bodySilhouette from "@/assets/body-silhouette.png";
import patientWomanA from "@assets/patient_woman_new1.png";
import patientWomanB from "@assets/patient_woman_new2.png";
import whyusLuxuryBg from "@assets/Adsız_tasarım_1777880438801.png";
import journeyProtocolBg from "@/assets/journey-protocol-bg.png";
import journeyBg from "@assets/whymen_1777375297047.png";
import pressMemorial from "@assets/1_1777877850504.png";
import pressWashTimes from "@assets/2_1777877850504.png";
import pressYahoo from "@assets/3_1777877850504.png";
import pressIBT from "@assets/4_1777877850504.png";
import pressLifeStyle from "@assets/5_1777877850504.png";
import pressNYWeekly from "@assets/6_1777877850505.png";
import pressDailyCaller from "@assets/7_1777877850505.png";
import pressTechBullion from "@assets/8_1777877850505.png";
import pressFirst from "@assets/9_1777877850505.png";
import pressBenzinga from "@assets/10_1777877850506.png";
import pressMarketsInsider from "@assets/11_1777877850506.png";
import pressFox8 from "@assets/12_1777877850506.png";
import pressWikipedia from "@assets/13_1777877850506.png";

import coverPlasticSurgeryUk from "@/assets/cover_plastic_surgery_uk.jpg";
import coverHairTransplantAu from "@/assets/cover_hair_transplant_au.jpg";
import coverGastricSleeveUk from "@/assets/cover_gastric_sleeve_uk.jpg";
import coverMommyMakeoverDe from "@/assets/cover_mommy_makeover_de.jpg";
import coverHairRhinoFr from "@/assets/cover_hair_rhino_fr.jpg";

void sixpackBg;
void luxuryVipBg;
void whyusVipBg;
void journeyProtocolBg;
void journeyBg;

const WHATSAPP_LINK =
  "https://api.whatsapp.com/send/?phone=905491354124&text&type=phone_number&app_absent=0";
const GOOGLE_LINK = "https://share.google/2PhqUDVyBxUJENHzT";
const TRUSTPILOT_LINK = "https://www.trustpilot.com/review/rememore.com";
const PHONE_LINK = "tel:+905491354124";
const EMAIL_LINK = "mailto:info@rememore.com";

const NAV_LINKS: { id: string; label: string; Icon: LucideIcon }[] = [
  { id: "why-us", label: "Why Us", Icon: ShieldCheck },
  { id: "procedures", label: "Procedures", Icon: Stethoscope },
  { id: "plan", label: "Plan", Icon: Sparkles },
  { id: "istanbul", label: "Destination", Icon: MapPin },
  { id: "hospitals", label: "Hospitals", Icon: Building2 },
  { id: "faq", label: "FAQ", Icon: HelpCircle },
];

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const formSchema = z.object({
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  dialCode: z.string().min(2, "Country code is required"),
  phone: z.string().min(5, "Phone number is required"),
  operation: z.string().optional(),
  consent: z.boolean().refine((val) => val, "Consent is required"),
  recaptchaToken: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;


/* Hero form: simplified flat operation categories */
const HERO_OPERATIONS: string[] = [
  "Aesthetic & Plastic Surgery",
  "Hair Transplant",
  "Obesity Surgery",
  "Dental Care",
  "Male Aesthetics",
  "Limb Lengthening",
  "Eye Treatments",
  "Other",
];

/* Country dial-code list — flag + ISO + dial code */
const COUNTRY_CODES: { code: string; iso: string; dial: string; flag: string }[] = [
  { code: "GB", iso: "UK", dial: "+44", flag: "🇬🇧" },
  { code: "US", iso: "US", dial: "+1",  flag: "🇺🇸" },
  { code: "CA", iso: "CA", dial: "+1",  flag: "🇨🇦" },
  { code: "AE", iso: "UAE", dial: "+971", flag: "🇦🇪" },
  { code: "SA", iso: "KSA", dial: "+966", flag: "🇸🇦" },
  { code: "QA", iso: "QA", dial: "+974", flag: "🇶🇦" },
  { code: "KW", iso: "KW", dial: "+965", flag: "🇰🇼" },
  { code: "BH", iso: "BH", dial: "+973", flag: "🇧🇭" },
  { code: "OM", iso: "OM", dial: "+968", flag: "🇴🇲" },
  { code: "DE", iso: "DE", dial: "+49", flag: "🇩🇪" },
  { code: "FR", iso: "FR", dial: "+33", flag: "🇫🇷" },
  { code: "IT", iso: "IT", dial: "+39", flag: "🇮🇹" },
  { code: "ES", iso: "ES", dial: "+34", flag: "🇪🇸" },
  { code: "NL", iso: "NL", dial: "+31", flag: "🇳🇱" },
  { code: "BE", iso: "BE", dial: "+32", flag: "🇧🇪" },
  { code: "CH", iso: "CH", dial: "+41", flag: "🇨🇭" },
  { code: "AT", iso: "AT", dial: "+43", flag: "🇦🇹" },
  { code: "SE", iso: "SE", dial: "+46", flag: "🇸🇪" },
  { code: "NO", iso: "NO", dial: "+47", flag: "🇳🇴" },
  { code: "DK", iso: "DK", dial: "+45", flag: "🇩🇰" },
  { code: "FI", iso: "FI", dial: "+358", flag: "🇫🇮" },
  { code: "IE", iso: "IE", dial: "+353", flag: "🇮🇪" },
  { code: "PT", iso: "PT", dial: "+351", flag: "🇵🇹" },
  { code: "PL", iso: "PL", dial: "+48", flag: "🇵🇱" },
  { code: "CZ", iso: "CZ", dial: "+420", flag: "🇨🇿" },
  { code: "GR", iso: "GR", dial: "+30", flag: "🇬🇷" },
  { code: "TR", iso: "TR", dial: "+90", flag: "🇹🇷" },
  { code: "RU", iso: "RU", dial: "+7",  flag: "🇷🇺" },
  { code: "UA", iso: "UA", dial: "+380", flag: "🇺🇦" },
  { code: "IL", iso: "IL", dial: "+972", flag: "🇮🇱" },
  { code: "EG", iso: "EG", dial: "+20", flag: "🇪🇬" },
  { code: "MA", iso: "MA", dial: "+212", flag: "🇲🇦" },
  { code: "ZA", iso: "ZA", dial: "+27", flag: "🇿🇦" },
  { code: "NG", iso: "NG", dial: "+234", flag: "🇳🇬" },
  { code: "KE", iso: "KE", dial: "+254", flag: "🇰🇪" },
  { code: "AU", iso: "AU", dial: "+61", flag: "🇦🇺" },
  { code: "NZ", iso: "NZ", dial: "+64", flag: "🇳🇿" },
  { code: "JP", iso: "JP", dial: "+81", flag: "🇯🇵" },
  { code: "KR", iso: "KR", dial: "+82", flag: "🇰🇷" },
  { code: "CN", iso: "CN", dial: "+86", flag: "🇨🇳" },
  { code: "HK", iso: "HK", dial: "+852", flag: "🇭🇰" },
  { code: "SG", iso: "SG", dial: "+65", flag: "🇸🇬" },
  { code: "MY", iso: "MY", dial: "+60", flag: "🇲🇾" },
  { code: "TH", iso: "TH", dial: "+66", flag: "🇹🇭" },
  { code: "ID", iso: "ID", dial: "+62", flag: "🇮🇩" },
  { code: "PH", iso: "PH", dial: "+63", flag: "🇵🇭" },
  { code: "IN", iso: "IN", dial: "+91", flag: "🇮🇳" },
  { code: "PK", iso: "PK", dial: "+92", flag: "🇵🇰" },
  { code: "BD", iso: "BD", dial: "+880", flag: "🇧🇩" },
  { code: "BR", iso: "BR", dial: "+55", flag: "🇧🇷" },
  { code: "MX", iso: "MX", dial: "+52", flag: "🇲🇽" },
  { code: "AR", iso: "AR", dial: "+54", flag: "🇦🇷" },
  { code: "CL", iso: "CL", dial: "+56", flag: "🇨🇱" },
  { code: "CO", iso: "CO", dial: "+57", flag: "🇨🇴" },
];

export default function Home() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const heroVideoRef = useRef<HTMLIFrameElement>(null);
  const istanbulVideoRef = useRef<HTMLVideoElement>(null);
  const faqColRef = useRef<HTMLDivElement>(null);
  const [faqHeight, setFaqHeight] = useState<number | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  // Hospital marquee — JS-driven so the user can drag with finger / cursor.
  const hospOuterRef = useRef<HTMLDivElement>(null);
  const hospInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const outer = hospOuterRef.current;
    const inner = hospInnerRef.current;
    if (!outer || !inner) return;

    let offset = 0;
    let half = 0;
    let last = performance.now();
    let dragging = false;
    let startX = 0;
    let startOffset = 0;
    let pointerId: number | null = null;
    const speedPxPerSec = 45;

    const measure = () => {
      half = inner.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(inner);

    let raf = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (!dragging && half > 0) {
        offset -= speedPxPerSec * dt;
      }
      if (half > 0) {
        while (offset <= -half) offset += half;
        while (offset > 0) offset -= half;
      }
      inner.style.transform = `translate3d(${offset}px,0,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onDown = (e: PointerEvent) => {
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startOffset = offset;
      try {
        outer.setPointerCapture(e.pointerId);
      } catch {}
      outer.style.cursor = "grabbing";
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging || pointerId !== e.pointerId) return;
      offset = startOffset + (e.clientX - startX);
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      pointerId = null;
      try {
        if (outer.hasPointerCapture(e.pointerId)) {
          outer.releasePointerCapture(e.pointerId);
        }
      } catch {}
      outer.style.cursor = "grab";
    };

    outer.style.cursor = "grab";
    outer.addEventListener("pointerdown", onDown);
    outer.addEventListener("pointermove", onMove);
    outer.addEventListener("pointerup", onUp);
    outer.addEventListener("pointercancel", onUp);
    outer.addEventListener("pointerleave", onUp);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      outer.removeEventListener("pointerdown", onDown);
      outer.removeEventListener("pointermove", onMove);
      outer.removeEventListener("pointerup", onUp);
      outer.removeEventListener("pointercancel", onUp);
      outer.removeEventListener("pointerleave", onUp);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track desktop breakpoint (lg = 1024px). Stories col only matches FAQ height on desktop.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Sync stories column height to FAQ column height (desktop only)
  useEffect(() => {
    const el = faqColRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setFaqHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Force-play both background videos via YouTube IFrame API postMessage.
  // Fires on load, on every YouTube event (onReady/onStateChange), and on a
  // timed fallback so the pause overlay is cleared regardless of autoplay policy.
  useEffect(() => {
    const playCmd = JSON.stringify({ event: "command", func: "playVideo", args: [] });

    const sendPlay = () => {
      heroVideoRef.current?.contentWindow?.postMessage(playCmd, "*");
      // Istanbul section uses an HTML5 <video> tag; force muted (React quirk
      // where the JSX prop sometimes does not stick) and nudge it to play.
      const v = istanbulVideoRef.current;
      if (v) {
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.play().catch(() => {});
      }
    };


    // Respond to any message from the YouTube players (onReady, onStateChange, etc.)
    const handleMessage = (e: MessageEvent) => {
      if (!e.data) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d.event === "onReady" || d.event === "onStateChange" || d.info !== undefined) {
          sendPlay();
        }
      } catch (_) {}
    };

    window.addEventListener("message", handleMessage);

    // Fire at 200 ms, 600 ms, 1.2 s, 2 s, 3 s after mount to catch slow-loading players
    const timers = [200, 600, 1200, 2000, 3000].map((ms) => setTimeout(sendPlay, ms));

    // Re-fire on every iframe load event (e.g., loop restart)
    const sendOnLoad = () => sendPlay();
    heroVideoRef.current?.addEventListener("load", sendOnLoad);
    istanbulVideoRef.current?.addEventListener("load", sendOnLoad);

    return () => {
      window.removeEventListener("message", handleMessage);
      timers.forEach(clearTimeout);
      heroVideoRef.current?.removeEventListener("load", sendOnLoad);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      istanbulVideoRef.current?.removeEventListener("load", sendOnLoad);
    };
  }, []);

  useEffect(() => {
    const scrollFromHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      setTimeout(() => tryScroll(), 200);
    };
    scrollFromHash();
    window.addEventListener("hashchange", scrollFromHash);
    return () => window.removeEventListener("hashchange", scrollFromHash);
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      dialCode: "+44:GB",
      phone: "",
      operation: "",
      consent: false,
    },
  });

  const getUrlParameter = function getUrlParameter(sParam:any) {
    let sPageURL = window.location.search.substring(1),
      sURLVariables = sPageURL.split('&'),
      sParameterName,
      i;

    for (i = 0; i < sURLVariables.length; i++) {
      sParameterName = sURLVariables[i].split('=');

      if (sParameterName[0] === sParam) {
        return sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
      }
    }
    return "";
  };

  function get_ga_clientid(): string {
    const gaCookie = document.cookie
      .split(";")
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith("_ga="));

    return gaCookie ? gaCookie.split("=")[1] : "";
  }

  async function sha256(value: string): Promise<string> {
    const normalizedValue = value.trim().toLowerCase();

    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedValue);

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  async function onSubmit(
        values: z.infer<typeof formSchema> & { recaptchaToken?: string }
      ) {
        try {
          const nameParts = values.fullName.trim().split(" ");
          const name = nameParts[0];
          const surname = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

          const formData = new FormData();

          formData.append("Name", name);
          formData.append("Surname", surname);
          formData.append("Email", values.email);
          formData.append("Phone", values.phone);
          formData.append("Prefix", "+44");
          formData.append("Description", "Operation: Male Aesthetics");
          formData.append("Language", "100000011");
          formData.append("CaseOriginCode", "100000007");
          formData.append("DoctorId", "");
          formData.append("HospitalId", "");
          formData.append("SectionId", "");
          formData.append("SubOriginCode", "100000000");
          formData.append("Subject", "100000003");
          formData.append("SourceUrl", typeof window !== "undefined" ? window.location.href : "");
          formData.append("Campaign", "");
          formData.append("g-recaptcha-response", values.recaptchaToken || "");
          formData.append("Utm_Source", String(getUrlParameter("utm_source")));
          formData.append("Utm_Campaign", String(getUrlParameter("utm_campaign")));
          formData.append("Utm_Content", String(getUrlParameter("utm_content")));
          formData.append("Utm_Medium", String(getUrlParameter("utm_medium")));
          formData.append("Utm_Term", String(getUrlParameter("utm_term")));
          formData.append("FacebookAdSet", "");
          formData.append("LeadChannel", "100000004");
          formData.append("LeadSource", "100000000");
          formData.append("isKvkkApproved", values.consent ? "true" : "false");
          formData.append("isETKApproved", "true");
          formData.append("FacebookUsername", "1");
          formData.append("InstagramUsername", "1");
          formData.append("FormSourceCode", "100000005");
          formData.append("RequestId", "42853ead-014f-4197-b732-18976df7507c");
          formData.append("Ga_UserId", "af67be2b8ad921d859b160ee676653f3bec9a41c8a0de1e8a20ea3d60cc260e0");
          formData.append("Ga_ClientId", get_ga_clientid());
          formData.append("Country", "United Kingdom");
          formData.append("Type", "undefined");
          formData.append("Documents", "");
          formData.append("lang2IsoCode", "en");
          formData.append("CookieGoogle", "undefined");
          formData.append("CookieFacebook1", "undefined");
          formData.append("CookieFacebook2", "undefined");
          formData.append("TiktokCookie1", "undefined");
          formData.append("TiktokCookie2", "undefined");
          

          const response = await fetch("/form", {
            method: "POST",
            headers: {
              "X-Referer": typeof window !== "undefined" ? window.location.href : "",
            },
            body: formData,
          });

          if (response.ok) {
            const hashedEmail = await sha256(values.email);

            window.dataLayer = window.dataLayer || [];

            window.dataLayer.push({
              event: "form_submission",
              form_id: "plastic_surgery",
              email: hashedEmail,
              form_status: "success",
            });

            setFormSuccess(true); 
            form.reset();
          } else {
            console.error("CRM send error:", response.statusText);
            alert("An error occurred, please try again later.");
          }
        } catch (error) {
          console.error("An error occurred during submission:", error);
          alert("A connection error occurred.");
        }
      }

  const fadeIn: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const scrollTo = (id: string) => {
    setIsNavOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openWhatsApp = () => window.open(WHATSAPP_LINK, "_blank");

  const openConsultation = () => {
    setIsNavOpen(false);
    setFormSuccess(false);
    setFormOpen(true);
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LcRuqgpAAAAAKCcgyREbGkcKQbdGQQ4mtn2M1f5" scriptProps={{ async: true, defer: true, appendTo: "head", nonce: undefined, }} >
      <div className="min-h-[100dvh] bg-background flex flex-col font-sans">
        {/* Top Nav */}
        <header
          className={`fixed top-0 w-full z-50 transition-all duration-300 ${
            scrolled
              ? "bg-background/85 backdrop-blur-xl border-b border-border/40 py-3"
              : "bg-transparent py-5"
          }`}
        >
          <div className="container mx-auto px-6 flex items-center justify-between gap-6">
            <a
              href="#"
              className="inline-block flex-shrink-0"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("hero");
              }}
            >
              <img
                src={lightLogo}
                alt="rememore"
                className="h-7 md:h-8 object-contain translate-y-[2px] md:translate-y-[3px]"
              />
            </a>

            <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold tracking-wide text-foreground/85">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                variant="outline"
                className="border-white/15 hover:bg-white/5 hover:text-white rounded-full h-11 px-5 font-semibold text-sm bg-white/[0.02]"
                onClick={openWhatsApp}
              >
                <FaWhatsapp className="mr-2 w-4 h-4 text-green-400" />
                WhatsApp
              </Button>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 h-11 font-bold tracking-wide text-sm shadow-[0_0_20px_rgba(200,165,110,0.25)]"
                onClick={openConsultation}
              >
                Book Consultation
              </Button>
            </div>

            <button
              className="lg:hidden text-foreground"
              onClick={() => setIsNavOpen(!isNavOpen)}
              aria-label="Toggle menu"
            >
              {isNavOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </header>

        {/* Mobile Nav Overlay */}
        <AnimatePresence>
          {isNavOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-gradient-to-b from-background/97 via-background/95 to-background/97 backdrop-blur-2xl pt-24 pb-10 px-5 lg:hidden overflow-y-auto"
            >
              {/* Decorative ambient glow */}
              <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-primary/15 blur-[100px]"></div>

              <div className="relative max-w-md mx-auto">
                {/* Header tag */}
                <div className="flex items-center justify-center mb-6">
                  <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3.5 py-1.5 bg-white/[0.04] backdrop-blur-md">
                    <Lock className="w-3 h-3 text-primary" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-foreground/70 font-extrabold">
                      Discreet & Private
                    </span>
                  </div>
                </div>

                <nav className="flex flex-col gap-2">
                  {NAV_LINKS.map((link, i) => (
                    <motion.button
                      key={link.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                      onClick={() => scrollTo(link.id)}
                      className="group flex items-center gap-4 w-full text-left bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-2xl px-4 py-3.5 transition-all"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <link.Icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-base font-extrabold tracking-wide text-foreground/90 group-hover:text-white transition-colors flex-1">
                        {link.label}
                      </span>
                      <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </motion.button>
                  ))}
                </nav>

                {/* CTAs */}
                <div className="mt-7 flex flex-col gap-3">
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-14 text-sm w-full font-extrabold tracking-[0.15em] uppercase shadow-[0_0_30px_rgba(200,165,110,0.4)]"
                    onClick={openConsultation}
                  >
                    Book Consultation
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/15 hover:bg-white/5 rounded-full h-14 text-sm w-full font-extrabold tracking-[0.15em] uppercase bg-white/[0.03]"
                    onClick={openWhatsApp}
                  >
                    <FaWhatsapp className="mr-2 w-5 h-5 text-green-400" />
                    WhatsApp Now
                  </Button>
                </div>

                {/* Trust mini-row */}
                <div className="mt-8 flex items-center justify-center gap-4 opacity-70">
                  <div className="flex items-center gap-1.5">
                    <SiTrustpilot className="w-3 h-3 text-green-400" />
                    <span className="text-[10px] uppercase tracking-wider font-bold">4.8</span>
                  </div>
                  <div className="w-px h-3 bg-white/15"></div>
                  <div className="flex items-center gap-1.5">
                    <FcGoogle className="w-3 h-3" />
                    <span className="text-[10px] uppercase tracking-wider font-bold">4.9</span>
                  </div>
                  <div className="w-px h-3 bg-white/15"></div>
                  <div className="flex items-center gap-1.5">
                    <Award className="w-3 h-3 text-primary" />
                    <span className="text-[10px] uppercase tracking-wider font-bold">JCI</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <main className="flex-grow">
          {/* HERO */}
          <section
            id="hero"
            className="relative min-h-[88dvh] flex items-center pt-24 pb-12 overflow-hidden"
          >
            <div className="absolute inset-x-0 top-0 h-[70dvh] md:inset-0 md:h-auto z-0 bg-background pointer-events-none overflow-hidden">
              <iframe
                ref={heroVideoRef}
                src="https://www.youtube.com/embed/gUFbMEXQuzQ?autoplay=1&mute=1&loop=1&playlist=gUFbMEXQuzQ&controls=0&showinfo=0&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&disablekb=1&fs=0&start=3&enablejsapi=1&cc_load_policy=0&hl=en"
                className="absolute opacity-100 pointer-events-none hero-video-iframe"
                style={{
                  top: "50%",
                  left: "50%",
                  clipPath: "inset(0 0 9% 0)",
                }}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
                title="Hero background video"
                tabIndex={-1}
                aria-hidden="true"
              />
              {/* Edge scrims — softened so the video reads brightly */}
              <div className="absolute inset-0 z-10 bg-gradient-to-r from-background/75 via-background/15 to-transparent pointer-events-none"></div>
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-background/30 to-transparent pointer-events-none"></div>
              {/* Bottom-only fade so hero blends smoothly into the next section without darkening the middle */}
              <div
                className="absolute inset-x-0 bottom-0 h-[45%] z-10 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to top, hsl(var(--background)) 0%, hsl(var(--background)/0.7) 35%, hsl(var(--background)/0.25) 70%, transparent 100%)",
                }}
              ></div>
              {/* Brief fading center scrim — hides the YouTube initial play button while autoplay kicks in, then disappears */}
              <div className="absolute inset-0 z-20 pointer-events-none yt-play-suppress"></div>
            </div>

            <div className="container relative z-20 px-6 mx-auto grid lg:grid-cols-2 gap-14 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="max-w-2xl"
              >
                <motion.div
                  variants={fadeIn}
                  className="flex items-center gap-3 mb-6"
                >
                  <div className="w-6 h-[2px] bg-primary rounded-full"></div>
                  <span className="gold-shimmer-bright text-xs font-bold tracking-[0.2em] uppercase">
                    Memorial Hospitals · Health Tourism
                  </span>
                </motion.div>

                <motion.h1
                  variants={fadeIn}
                  className="font-sans font-extrabold leading-[1.02] mb-5 md:mb-8 text-foreground flex flex-col tracking-tight"
                >
                  <span className="text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem]">
                    PLASTIC SURGERY
                  </span>
                  <span className="text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] text-primary">
                    IN TURKEY
                  </span>
                </motion.h1>

                <motion.p
                  variants={fadeIn}
                  className="text-[11px] sm:text-base md:text-xl text-foreground/85 mb-6 md:mb-10 leading-relaxed font-medium max-w-xl whitespace-nowrap md:whitespace-normal"
                >
                  The Health Tourism brand of Memorial Hospitals Group.<br />
                  World-class plastic surgery inside JCI-accredited<br />
                  hospitals — VIP, multilingual, fully concierged.
                </motion.p>

                {/* MOBILE-ONLY consultation form (same style as desktop, in hero) */}
                <motion.div
                  variants={fadeIn}
                  className="lg:hidden w-full mb-6"
                >
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[2.5rem] blur-3xl pointer-events-none"></div>
                    <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[1.75rem] p-5 sm:p-6 shadow-[0_25px_60px_rgba(0,0,0,0.5)]">
                      <ConsultationCard
                        form={form}
                        onSubmit={onSubmit}
                        formSuccess={formSuccess}
                        resetForm={() => {
                          form.reset();
                          setFormSuccess(false);
                        }}
                        onPrivacyClick={() => setPrivacyOpen(true)}
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Desktop/tablet CTA buttons (mobile uses the form above + sticky bar) */}
                <motion.div
                  variants={fadeIn}
                  className="hidden sm:flex flex-col sm:flex-row gap-4 mb-10 w-full"
                >
                  <Button
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm shadow-[0_0_25px_rgba(200,165,110,0.35)]"
                    onClick={openConsultation}
                  >
                    Book Private Consultation
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20 text-foreground hover:bg-white/5 h-14 px-8 rounded-full font-bold tracking-wide uppercase text-sm bg-white/[0.03] backdrop-blur-sm"
                    onClick={openWhatsApp}
                  >
                    <FaWhatsapp className="mr-2 w-5 h-5 text-green-400" />{" "}
                    WhatsApp Now
                  </Button>
                </motion.div>

                {/* MOBILE — separated trust tags (3 pills, single row) */}
                <motion.div
                  variants={fadeIn}
                  className="sm:hidden flex flex-nowrap items-stretch gap-1.5 w-full"
                >
                  <a
                    href={TRUSTPILOT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 hover:bg-white/[0.09] transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
                  >
                    <div className="bg-green-500/20 p-0.5 rounded-full flex-shrink-0">
                      <SiTrustpilot className="w-2.5 h-2.5 text-green-400" />
                    </div>
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                        Trustpilot
                      </span>
                      <span className="text-[9px] font-bold whitespace-nowrap">4.8 / 5</span>
                    </div>
                  </a>
                  <a
                    href={GOOGLE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 hover:bg-white/[0.09] transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
                  >
                    <div className="bg-white/10 p-0.5 rounded-full flex-shrink-0">
                      <FcGoogle className="w-2.5 h-2.5" />
                    </div>
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                        Google
                      </span>
                      <span className="text-[9px] font-bold whitespace-nowrap">4.9 · 220+</span>
                    </div>
                  </a>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]">
                    <div className="bg-primary/20 p-0.5 rounded-full flex-shrink-0">
                      <Award className="w-2.5 h-2.5 text-primary" />
                    </div>
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-[7px] uppercase tracking-wider text-foreground/60 font-bold">
                        JCI
                      </span>
                      <span className="text-[9px] font-bold whitespace-nowrap">Certified</span>
                    </div>
                  </div>
                </motion.div>

                {/* DESKTOP/TABLET — single pill trust strip */}
                <motion.div
                  variants={fadeIn}
                  className="hidden sm:flex flex-wrap items-center justify-between gap-x-7 gap-y-4 bg-white/[0.06] backdrop-blur-2xl border border-white/15 rounded-full px-7 py-3.5 w-full shadow-[inset_0_1px_0_rgba(255,255,255,0.10),inset_0_-1px_0_rgba(0,0,0,0.25),0_10px_30px_rgba(0,0,0,0.35)]"
                >
                  <a
                    href={TRUSTPILOT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <div className="bg-green-500/20 p-1.5 rounded-full">
                      <SiTrustpilot className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                        Trustpilot
                      </span>
                      <span className="text-xs font-bold">
                        Excellent · 4.8 / 5
                      </span>
                    </div>
                  </a>
                  <div className="w-px h-8 bg-white/10 hidden sm:block"></div>
                  <a
                    href={GOOGLE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <div className="bg-white/10 p-1.5 rounded-full">
                      <FcGoogle className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                        Google Reviews
                      </span>
                      <span className="text-xs font-bold">4.9 / 5 · 220+</span>
                    </div>
                  </a>
                  <div className="w-px h-8 bg-white/10 hidden sm:block"></div>
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/20 p-1.5 rounded-full">
                      <Award className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-foreground/60 font-bold">
                        Accreditation
                      </span>
                      <span className="text-xs font-bold">JCI Certified</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="lg:ml-auto w-full max-w-md hidden lg:block"
              >
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
                  <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-7 md:p-8 shadow-[0_25px_60px_rgba(0,0,0,0.5)]">
                    <ConsultationCard
                      form={form}
                      onSubmit={onSubmit}
                      formSuccess={formSuccess}
                      resetForm={() => {
                        form.reset();
                        setFormSuccess(false);
                      }}
                      onPrivacyClick={() => setPrivacyOpen(true)}
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* AS SEEN IN — press logo strip flowing under the hero */}
          <PressStrip />

          {/* THE STANDARD + BY THE NUMBERS — one continuous section, shared luxury background */}
          <div
            className="relative surface-lighter overflow-hidden"
            style={{
              ["--cursor-x" as never]: "50%",
              ["--cursor-y" as never]: "50%",
              ["--cursor-opacity" as never]: "0",
            }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              e.currentTarget.style.setProperty("--cursor-x", `${e.clientX - rect.left}px`);
              e.currentTarget.style.setProperty("--cursor-y", `${e.clientY - rect.top}px`);
              e.currentTarget.style.setProperty("--cursor-opacity", "1");
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.setProperty("--cursor-opacity", "0");
            }}
          >
            {/* Cursor-following blurry gold shine — spans both subsections */}
            <div
              className="absolute pointer-events-none rounded-full z-[1]"
              style={{
                left: "var(--cursor-x)",
                top: "var(--cursor-y)",
                opacity: "var(--cursor-opacity)",
                transform: "translate(-50%, -50%)",
                width: "560px",
                height: "560px",
                background:
                  "radial-gradient(circle, rgba(230,220,200,0.18), rgba(230,220,200,0.05) 40%, transparent 70%)",
                filter: "blur(70px)",
                transition: "opacity 350ms ease",
                willChange: "transform, opacity",
              }}
            />
            {/* Shared background image + vignettes spanning both subsections */}
            <div className="absolute inset-0 pointer-events-none">
              <img
                src={whyusLuxuryBg}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover opacity-70 md:opacity-55"
              />
              {/* Mobile vignette — light, so the image clearly reads behind the glass cards */}
              <div
                className="absolute inset-0 md:hidden"
                style={{
                  background:
                    "radial-gradient(ellipse 75% 70% at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.04) 90%, transparent 100%)",
                }}
              ></div>
              {/* Desktop vignette — gentle so the photo stays visible through the cards */}
              <div
                className="absolute inset-0 hidden md:block"
                style={{
                  background:
                    "radial-gradient(ellipse 55% 60% at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.08) 80%, transparent 100%)",
                }}
              ></div>
              {/* Top darken (nav seam) */}
              <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent"></div>
              {/* Bottom dark fade — strong, so the section ends in deep black before the next section */}
              <div className="absolute inset-x-0 bottom-0 h-48 md:h-64 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
            </div>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(200,165,110,0.08),transparent_60%)] pointer-events-none"></div>

          {/* WHY US */}
          <section
            id="why-us"
            className="pt-8 md:pt-12 pb-0 md:pb-1 relative scroll-mt-24 overflow-hidden"
          >
            <div className="container mx-auto px-6 relative">
              <SectionHeader
                eyebrow="01 — The Standard"
                uppercase
                divider
                title={
                  <>
                    Why Choose <span className="gold-shimmer-bright">Us</span>
                  </>
                }
              />

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-3 sm:gap-4 max-w-7xl mx-auto mt-3 sm:mt-4">
                {/* LEFT: 2x2 grid of reasons */}
                <div className="grid grid-cols-2 gap-2.5 sm:gap-5">
                  {[
                    {
                      num: "01",
                      Icon: Globe2,
                      title: "24/7 Multilingual",
                      desc: "Personal interpreters at every meeting, transfer and recovery — in your language, around the clock.",
                      stat: "24/7",
                      sub: "Care",
                    },
                    {
                      num: "02",
                      Icon: Stethoscope,
                      title: "Master Surgeons",
                      desc: "1,200+ board-certified, internationally trained specialists in plastic and reconstructive surgery.",
                      stat: "1,200+",
                      sub: "Doctors",
                    },
                    {
                      num: "03",
                      Icon: Plane,
                      title: "Full VIP Concierge",
                      desc: "Flights, VIP transfers and 5-star hotel stays — everything but the surgery, handled.",
                      stat: "5★",
                      sub: "Hotels",
                    },
                    {
                      num: "04",
                      Icon: Award,
                      title: "JCI Accredited",
                      desc: "Memorial Hospitals Group — Turkey's largest private network, audited yearly to JCI standards.",
                      stat: "98%",
                      sub: "Satisfaction",
                    },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      className="relative bg-white/[0.05] hover:bg-white/[0.22] backdrop-blur-2xl border border-white/15 hover:border-primary/30 rounded-2xl p-3 sm:p-4 hover:-translate-y-1 transition-all duration-500 group overflow-hidden"
                      style={{
                        boxShadow:
                          "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.25)",
                      }}
                    >
                      {/* Liquid-glass inner top-left highlight */}
                      <div
                        className="pointer-events-none absolute inset-0 rounded-2xl opacity-50 group-hover:opacity-90 transition-opacity duration-500"
                        style={{
                          background:
                            "radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.10) 0%, transparent 55%)",
                        }}
                      ></div>
                      {/* Animated gold border-shine on hover (rotates around the card stroke) */}
                      <div className="glass-shine-border"></div>
                      <span className="absolute top-2 right-2 sm:top-4 sm:right-5 font-sans font-extrabold text-2xl sm:text-5xl text-white/[0.06] group-hover:text-primary/25 transition-colors leading-none">
                        {item.num}
                      </span>
                      <div className="w-8 h-8 sm:w-11 sm:h-11 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center mb-2 sm:mb-4 group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                        <item.Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <h3 className="text-[11px] sm:text-base font-extrabold uppercase tracking-wide mb-1.5 sm:mb-2 leading-tight">
                        {item.title}
                      </h3>
                      <p className="hidden sm:block text-[11px] sm:text-sm text-foreground/70 font-medium leading-relaxed mb-2 sm:mb-2.5 sm:min-h-[60px]">
                        {item.desc}
                      </p>
                      <div className="border-t border-white/10 pt-2 sm:pt-3 flex items-baseline gap-1.5 sm:gap-2">
                        <span className="gold-shimmer-light text-base sm:text-xl font-extrabold tracking-tight">
                          {item.stat}
                        </span>
                        <span className="text-[8px] sm:text-[10px] uppercase tracking-widest text-foreground/55 font-bold">
                          {item.sub}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* RIGHT: YouTube hover-mute / click-unmute video card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <WhyUsVideoCard />
                </motion.div>
              </div>

              <div className="flex justify-center mt-8 sm:mt-12 mb-4 sm:mb-6">
                <Button
                  onClick={openConsultation}
                  className="relative overflow-hidden cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 h-12 sm:h-14 px-8 sm:px-10 rounded-full font-bold tracking-[0.2em] uppercase text-xs sm:text-sm shadow-[0_0_25px_rgba(200,165,110,0.35)] group/cta"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 -left-full w-full -skew-x-12 opacity-0 group-hover/cta:opacity-100 group-hover/cta:translate-x-[220%] transition-all duration-[1000ms] ease-out"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 35%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.15) 65%, transparent 100%)",
                    }}
                  />
                  <span className="relative z-10 gold-shimmer-soft">Choose Us</span>
                </Button>
              </div>
            </div>
          </section>

          {/* BY THE NUMBERS */}
          <section
            id="numbers"
            className="pt-3 md:pt-4 pb-5 md:pb-7 relative overflow-hidden scroll-mt-24 section-no-fade"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(200,165,110,0.06),transparent_60%)] pointer-events-none"></div>
            <div className="container mx-auto px-6 relative z-10">
              <div className="grid grid-cols-2 gap-y-8 gap-x-4 md:flex md:flex-nowrap md:items-center md:justify-around md:gap-1 md:gap-y-10 max-w-6xl mx-auto md:overflow-visible">
                {[
                  { value: 700, suffix: "K+", display: "700K+", label: "Patients / Year" },
                  { value: 1200, suffix: "+", display: "1,200+", label: "Qualified Doctors" },
                  { value: 25, suffix: "+", display: "25+", label: "Years Experience" },
                  { value: 13, suffix: "", display: "13", label: "Memorial Hospitals" },
                  { value: 4.8, suffix: "/5", display: "4.8/5", label: "Patient Rating", decimals: 1 },
                ].map((stat, i, arr) => (
                  <React.Fragment key={i}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.5, delay: i * 0.08 }}
                      className={`relative flex flex-col items-center text-center px-1.5 md:px-4 md:min-w-[140px] group md:flex-shrink-0 ${
                        i === arr.length - 1 ? "col-span-2 md:col-span-1" : ""
                      }`}
                    >
                      <div className="text-3xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold mb-1 md:mb-3 leading-none tracking-tight gold-shimmer-light whitespace-nowrap">
                        <CountUp
                          end={stat.value}
                          suffix={stat.suffix}
                          decimals={stat.decimals ?? 0}
                          formatThousands={stat.value >= 1000}
                        />
                      </div>
                      <div className="text-[10px] sm:text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.25em] text-foreground/70 font-bold leading-tight">
                        {stat.label}
                      </div>
                    </motion.div>
                    {i < arr.length - 1 && (
                      <div className="hidden md:block w-px h-16 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>
          </div>

          {/* PROCEDURES */}
          <section
            id="procedures"
            className="py-20 md:py-24 surface-deep relative scroll-mt-24 overflow-hidden"
          >
            {/* Bleed from why-us above — soft top fade so the two sections merge */}
            <div className="absolute inset-x-0 -top-px h-40 bg-gradient-to-b from-black/70 via-black/35 to-transparent pointer-events-none z-[1]"></div>
            {/* High-contrast accent washes for premium card pop */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(200,165,110,0.12),transparent_55%)] pointer-events-none"></div>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(200,165,110,0.08),transparent_55%)] pointer-events-none"></div>
            <div className="container mx-auto px-6 relative z-10">
              <SectionHeader
                eyebrow="02 — Procedures"
                title={
                  <>
                    Signature <span className="text-primary">Procedures</span>
                  </>
                }
                subtitle="Six flagship treatment families, each performed by senior specialists inside JCI-accredited Memorial Hospitals."
                subtitleClassName="text-[11px] md:text-base max-w-md md:max-w-none mx-auto"
              />

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 mt-10 md:mt-16">
                <ProcedureCard
                  img={procFacial}
                  num="01"
                  title="Facial Surgery"
                  desc="Rhinoplasty, facelift, neck lift, blepharoplasty and brow procedures — refined facial harmony with natural, lasting results."
                  features={[
                    "Rhinoplasty",
                    "Face & neck lift",
                    "Blepharoplasty",
                    "Brow lift · Fox eyes",
                    "Forehead · Lip lift",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
                <ProcedureCard
                  img={procBody}
                  num="02"
                  title="Body Surgery"
                  desc="Liposuction, BBL, tummy tuck, mommy makeover and total body beautification — sculpted contours with a single coordinated journey."
                  features={[
                    "Liposuction · BBL",
                    "Tummy tuck",
                    "Mommy makeover",
                    "Arm · Leg lift",
                    "Fat transfer",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
                <ProcedureCard
                  img={procBreast}
                  num="03"
                  title="Breast Surgery"
                  desc="Augmentation, lift, reduction, reconstruction and gynecomastia — surgical artistry tailored to your proportions and goals."
                  features={[
                    "Augmentation",
                    "Lift & reduction",
                    "Reconstruction",
                    "Gynecomastia",
                    "Breast implant",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
                <ProcedureCard
                  img={procHair}
                  num="04"
                  title="Hair Transplantation"
                  desc="DHI, Sapphire FUE and unshaved techniques — natural-looking density restored by Turkey's most experienced hair surgeons."
                  features={[
                    "DHI · FUE",
                    "Beard transplant",
                    "Eyebrow transplant",
                    "Hair PRP",
                    "Stem cell hair",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
                <ProcedureCard
                  img={procDental}
                  num="05"
                  title="Dental Treatments"
                  desc="Hollywood smile, dental implants and luxury cosmetic dentistry — performed in our dedicated dental clinics."
                  features={[
                    "Hollywood smile",
                    "Dental implants",
                    "Veneers · Laminate",
                    "Zirconium · Emax",
                    "Teeth whitening",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
                <ProcedureCard
                  img={procBariatric}
                  num="06"
                  title="Bariatric Surgery"
                  desc="Gastric sleeve, bypass, balloon and gastric botox — life-changing weight-loss programs with full pre- and post-op care."
                  features={[
                    "Gastric sleeve",
                    "Gastric bypass",
                    "Gastric balloon",
                    "Gastric botox",
                    "Full check-up",
                  ]}
                  openWhatsApp={openWhatsApp}
                  openConsultation={openConsultation}
                />
              </div>

            </div>
          </section>

          {/* BEFORE / AFTER GALLERY */}
          <section
            id="results"
            className="pt-12 md:pt-24 pb-12 md:pb-24 relative overflow-hidden scroll-mt-24 surface-deep"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(209,190,138,0.08),transparent_60%)] pointer-events-none"></div>
            <div className="container mx-auto px-3 md:px-6 relative z-10">
              <SectionHeader
                eyebrow="03 — Real Results"
                title={
                  <>
                    Before &amp; <span className="text-primary">After</span>
                  </>
                }
                subtitle="A small selection of recent patient outcomes — all photographed in our hospitals, shared with consent."
              />
              <BeforeAfterGallery />
            </div>
          </section>

          {/* VIDEO TESTIMONIALS GALLERY */}
          <section
            id="testimonials"
            className="pt-12 md:pt-24 pb-12 md:pb-24 relative overflow-hidden scroll-mt-24 surface-light"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(209,190,138,0.08),transparent_60%)] pointer-events-none"></div>
            <div className="container mx-auto px-3 md:px-6 relative z-10">
              <SectionHeader
                eyebrow="04 — Patient Voices"
                title={
                  <>
                    Video <span className="text-primary">Testimonials</span>
                  </>
                }
                subtitle="Hear from international patients in their own words — tap to play with sound."
              />
              <VideoTestimonialsGallery />
            </div>
          </section>

          {/* PERSONALIZED PLAN — standalone section 06 */}
          <section
            id="plan"
            className="pt-12 md:pt-24 pb-12 md:pb-24 relative overflow-hidden scroll-mt-24 surface-light"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(200,165,110,0.08),transparent_60%)] pointer-events-none"></div>
            <div className="container mx-auto px-3 md:px-6 relative z-10">
              <PersonalizedPlanSection
                onBook={openConsultation}
                onWhatsApp={openWhatsApp}
              />
            </div>
          </section>

          {/* ISTANBUL + JOURNEY (merged) — section 06 */}
          <section
            id="istanbul"
            className="relative min-h-[90vh] flex items-center py-10 md:py-14 overflow-hidden scroll-mt-24"
          >
            <div className="absolute inset-x-0 top-0 h-[55dvh] md:inset-0 md:h-auto z-0 bg-background pointer-events-none overflow-hidden">
              <video
                ref={istanbulVideoRef}
                src="https://player.vimeo.com/progressive_redirect/playback/1187967599/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=7802ae14eff2a4c084828b4f28c0f6fea2c90272da2050a0bb3a62344642a1ba"
                className="absolute inset-0 w-full h-full object-cover opacity-95 pointer-events-none"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                disablePictureInPicture
                controls={false}
                controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                tabIndex={-1}
                aria-hidden="true"
              />
              {/* Soft side + bottom blend — keeps the video bright while letting type read */}
              <div className="absolute inset-0 z-10 bg-gradient-to-r from-background/65 via-background/10 to-transparent pointer-events-none"></div>
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none"></div>
              {/* Brief fading center scrim — hides any initial play overlay while autoplay starts, then disappears */}
              <div className="absolute inset-0 z-20 pointer-events-none yt-play-suppress"></div>
            </div>

            <div className="container mx-auto px-6 relative z-20">
              <div className="max-w-2xl mb-8">
                <div className="inline-block border border-white/15 rounded-full px-4 py-1 bg-white/[0.05] backdrop-blur-md mb-4">
                  <span className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
                    05 — The Destination
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 text-white leading-[1.05] tracking-tight">
                  Why <span className="text-primary">Istanbul</span>?
                </h2>
                <div className="text-white/90 text-base md:text-lg font-medium leading-relaxed">
                  <p>
                    The world's premier hub for advanced medical procedures —
                    elite European standards with discreet, high-end care.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {[
                  { stat: "1.5M+", label: "Medical Tourists per Year" },
                  { stat: "Top 10", label: "Global Medical Hub" },
                  { stat: "JCI", label: "Highest International Standard" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`bg-black/50 backdrop-blur-xl border border-white/15 rounded-2xl p-4 md:p-5 hover:-translate-y-1 hover:border-primary/40 transition-all duration-500 ${
                      i === 2 ? "col-span-2 md:col-span-1" : ""
                    }`}
                  >
                    <div className="text-2xl md:text-3xl font-extrabold text-primary mb-1">
                      {item.stat}
                    </div>
                    <div className="text-white/80 uppercase tracking-widest text-[10px] font-bold">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* JOURNEY PHASES — collapsed into Destination */}
              <div className="mt-10">
                <div className="max-w-2xl mb-6">
                  <h3 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-5">
                    Your <span className="text-primary">Journey</span>
                  </h3>
                  <p className="text-white/90 text-base md:text-lg font-medium leading-relaxed">
                    Five orchestrated phases, from first private message to lifetime aftercare.
                  </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                  {JOURNEY_PHASES.map((phase, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                      className={`relative bg-black/40 backdrop-blur-xl border border-white/15 rounded-2xl p-4 hover:-translate-y-1 hover:bg-black/55 hover:border-primary/40 transition-all duration-500 group overflow-hidden ${
                        i === JOURNEY_PHASES.length - 1 ? "col-span-2 lg:col-span-1" : ""
                      }`}
                    >
                      <span className="absolute top-3 right-4 font-sans font-extrabold text-4xl text-white/[0.06] group-hover:text-primary/20 transition-colors leading-none">
                        {phase.num}
                      </span>
                      <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/15 flex items-center justify-center mb-3 group-hover:bg-primary/10 group-hover:border-primary/30 transition-colors">
                        <phase.Icon className="w-4 h-4 text-primary" />
                      </div>
                      <h4 className="text-xs font-extrabold uppercase tracking-wide mb-1.5 leading-tight text-white">
                        {phase.title}
                      </h4>
                      <p className="text-[11px] text-white/70 font-medium leading-relaxed mb-3 min-h-[44px]">
                        {phase.desc}
                      </p>
                      <div className="border-t border-white/10 pt-2 flex items-baseline gap-2">
                        <span className="text-lg font-extrabold text-primary tracking-tight">
                          {phase.stat}
                        </span>
                        <span className="text-[9px] uppercase tracking-widest text-white/55 font-bold">
                          {phase.sub}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex justify-center mt-12 md:mt-16">
                  <Button
                    onClick={openConsultation}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-10 rounded-full font-bold tracking-[0.25em] uppercase text-sm shadow-[0_0_30px_rgba(200,165,110,0.4)]"
                  >
                    Start
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* HOSPITALS */}
          <section
            id="hospitals"
            className="py-20 md:py-24 relative scroll-mt-24 surface-deep overflow-hidden"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(200,165,110,0.07),transparent_60%)] pointer-events-none"></div>
            <div className="container mx-auto px-6 relative">
              <SectionHeader
                eyebrow="06 — Hospital Network"
                title={<>Memorial Hospitals Group</>}
                titleClassName="text-2xl md:text-5xl lg:text-6xl whitespace-nowrap"
                subtitle="Procedures performed inside JCI-accredited Memorial hospitals — Turkey's largest private healthcare network."
                subtitleClassName="text-[11px] md:text-base max-w-md md:max-w-2xl mx-auto"
              />

              {/* Single-row horizontal marquee — JS-driven, drag-to-scrub */}
              <div
                ref={hospOuterRef}
                className="mt-16 mb-12 overflow-hidden select-none touch-pan-y"
                style={{
                  maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
                  touchAction: "pan-y",
                }}
              >
                <div ref={hospInnerRef} className="flex gap-2.5 md:gap-5 w-max will-change-transform">
                  {[
                    { name: "Memorial Bahçelievler", loc: "Istanbul", tag: "Flagship · Plastic Surgery", flag: true,  img: hospBahcelievler },
                    { name: "Memorial Şişli",         loc: "Istanbul", tag: "Aesthetic Surgery Center",   img: hospSisli      },
                    { name: "Memorial Bodrum",        loc: "Bodrum",   tag: "Aegean Recovery Resort",     img: hospBodrum     },
                    { name: "Memorial Göztepe",       loc: "Istanbul", tag: "Ambulatory Surgery",         img: hospGoztepe    },
                    { name: "Memorial Ataşehir",      loc: "Istanbul", tag: "Premium Outpatient",         img: hospAtasehir   },
                    { name: "Memorial Ankara",        loc: "Ankara",   tag: "Capital Center",             img: hospAnkara     },
                    { name: "Memorial Bahçelievler", loc: "Istanbul", tag: "Flagship · Plastic Surgery", flag: true,  img: hospBahcelievler },
                    { name: "Memorial Şişli",         loc: "Istanbul", tag: "Aesthetic Surgery Center",   img: hospSisli      },
                    { name: "Memorial Bodrum",        loc: "Bodrum",   tag: "Aegean Recovery Resort",     img: hospBodrum     },
                    { name: "Memorial Göztepe",       loc: "Istanbul", tag: "Ambulatory Surgery",         img: hospGoztepe    },
                    { name: "Memorial Ataşehir",      loc: "Istanbul", tag: "Premium Outpatient",         img: hospAtasehir   },
                    { name: "Memorial Ankara",        loc: "Ankara",   tag: "Capital Center",             img: hospAnkara     },
                  ].map((hosp, i) => (
                    <div
                      key={i}
                      className={`flex-shrink-0 w-[10rem] md:w-72 relative bg-white/[0.04] backdrop-blur-xl border rounded-2xl group hover:-translate-y-1 hover:bg-white/[0.06] transition-all duration-500 overflow-hidden ${
                        hosp.flag ? "border-primary/40" : "border-white/10 hover:border-primary/30"
                      }`}
                    >
                      <div className="relative h-24 md:h-44 overflow-hidden bg-[#0d0d0d]">
                        <img
                          src={hosp.img}
                          alt={hosp.name}
                          loading="lazy"
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 select-none pointer-events-none"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        {hosp.flag && (
                          <span className="absolute top-2 left-2 md:top-4 md:left-4 text-[8px] md:text-[9px] font-extrabold tracking-[0.2em] uppercase bg-primary text-primary-foreground rounded-full px-2 md:px-2.5 py-0.5 md:py-1 shadow-lg z-10">
                            Flagship
                          </span>
                        )}
                        <div className="absolute bottom-2 left-2 md:bottom-3 md:left-4 flex items-center gap-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-white font-extrabold drop-shadow z-10">
                          <MapPin className="w-3 h-3" />
                          {hosp.loc}
                        </div>
                      </div>
                      <div className="p-3 md:p-5 min-h-[68px] md:min-h-[88px] flex items-start">
                        <h3 className="text-sm md:text-lg font-extrabold tracking-tight leading-tight">
                          {(() => {
                            const parts = hosp.name.split(" ");
                            return (
                              <>
                                {parts[0]}
                                <br />
                                {parts.slice(1).join(" ")}
                              </>
                            );
                          })()}
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* JCI Accreditation — collapsed into Hospital Network */}
              <div className="mt-14 pt-12 border-t border-white/10">
                <div className="grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-14 items-center max-w-5xl mx-auto">
                  <div className="flex justify-center">
                    <CertificateMedal />
                  </div>

                  <div>
                    <div className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-primary/85 font-extrabold mb-3">
                      Accredited Excellence
                    </div>
                    <h3 className="text-2xl md:text-3xl font-extrabold mb-3 leading-[1.05] tracking-tight">
                      Gold-Seal{" "}
                      <span className="text-primary">Joint Commission</span>{" "}
                      Accreditation
                    </h3>
                    <p className="text-base text-foreground/85 font-medium leading-relaxed mb-4">
                      Every procedure is performed inside JCI-accredited hospitals — the world's most rigorous patient-safety standard.
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                      {[
                        "Audited surgical safety protocols",
                        "US/EU sterile-environment standards",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-4 h-4 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-2.5 h-2.5 text-primary" />
                          </div>
                          <span className="text-sm font-medium text-foreground/90">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* FAQ + PATIENT STORIES — full-screen split, unified section header */}
          <section id="faq" className="py-10 md:py-20 relative scroll-mt-24 surface-deep overflow-hidden">
            <div className="container mx-auto px-6 w-full">
              <SectionHeader
                eyebrow="07 — Answers & Stories"
                title={<>Clear <span className="text-primary">Answers</span>,<br />Real <span className="text-primary">Results</span></>}
                align="left"
                titleClassName="text-3xl md:text-5xl lg:text-6xl"
              />
              <div className="text-foreground/70 text-xs md:text-base mt-3 mb-6 md:mb-10 max-w-2xl">
                Everything you need to know — alongside verified accounts from international patients. Identities anonymized.
              </div>

              <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-stretch">

                {/* LEFT — FAQ */}
                <div className="flex flex-col" ref={faqColRef} id="stories-anchor">
                  <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-4 md:p-8">
                    <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-2">
                      <FAQItem
                        value="faq-1"
                        q="What plastic surgery procedures do you offer in Turkey?"
                        a="The full spectrum of aesthetic and reconstructive plastic surgery — facial (rhinoplasty, facelift, blepharoplasty), body (liposuction, BBL, tummy tuck, mommy makeover), breast (augmentation, lift, reduction, gynecomastia) and combined procedures, plus hair transplantation, dental aesthetics and bariatric surgery."
                      />
                      <FAQItem
                        value="faq-2"
                        q="How long should I plan to stay in Turkey?"
                        a="Most plastic surgery journeys span 5 to 10 nights — long enough for pre-op assessments, surgery, monitored recovery and a final follow-up before flying home. Combined procedures or mommy makeovers may require 10 to 14 nights. Your coordinator confirms the exact stay during planning."
                      />
                      <FAQItem
                        value="faq-3"
                        q="Do you take care of international patients end to end?"
                        a="Yes. Rememore is a dedicated health-tourism brand. We coordinate flights, VIP airport transfers, 5-star hotel accommodation, 24/7 multilingual interpreters and every hospital appointment — so the only thing you arrange is the trip itself."
                      />
                      <FAQItem
                        value="faq-4"
                        q="Are the surgeons and hospitals truly accredited?"
                        a="Every procedure is performed inside Memorial Hospitals Group — Turkey's largest private network — with JCI (Joint Commission International) accreditation, ISO 15189 lab accreditation and LEED Platinum certified facilities. Surgeons are board-certified plastic-surgery specialists with international training."
                      />
                      <FAQItem
                        value="faq-5"
                        q="How long do plastic surgery results last?"
                        a="Surgical results — rhinoplasty, facelift, breast surgery, body contouring — are designed to be long-lasting. Outcomes are influenced by aging, weight changes and lifestyle, but our surgeons plan for results that look natural for many years and beyond."
                      />
                      <FAQItem
                        value="faq-6"
                        q="What are the risks of plastic surgery abroad?"
                        a="Every surgery carries some risk. Choosing a JCI-accredited Memorial hospital, board-certified senior surgeons and a dedicated post-op care plan dramatically reduces those risks. Rememore provides a transparent medical assessment and informed-consent process before any decision is made."
                      />
                    </Accordion>
                  </div>

                  <div className="bg-primary/[0.08] border border-primary/20 rounded-2xl md:rounded-3xl p-4 md:p-7 backdrop-blur-sm mt-4 md:mt-6 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 justify-between">
                    <p className="text-xs md:text-base font-medium text-foreground/85 leading-relaxed flex-1">
                      Have a specific question? Our medical coordinators are available 24/7.
                    </p>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 md:h-12 px-5 md:px-6 uppercase tracking-wider text-xs font-bold shadow-[0_0_20px_rgba(200,165,110,0.3)] flex-shrink-0"
                      onClick={openWhatsApp}
                    >
                      <FaWhatsapp className="mr-2 w-4 h-4" /> Ask on WhatsApp
                    </Button>
                  </div>
                </div>

                {/* RIGHT — Patient Stories (infinite vertical marquee, exactly 6 visible) */}
                <div
                  id="stories"
                  className="flex flex-col lg:justify-center scroll-mt-24 overflow-hidden"
                  style={isDesktop && faqHeight ? { height: faqHeight } : undefined}
                >
                  {/* Stories marquee — mobile: 4 cards × 104 + 3 gaps × 8 = 440px; desktop: 6 × 104 + 5 × 8 = 664px */}
                  <div
                    className="relative overflow-hidden h-[440px] lg:h-[664px]"
                    style={{
                      maskImage:
                        "linear-gradient(to bottom, transparent 0%, black 18%, black 92%, transparent 100%)",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent 0%, black 18%, black 92%, transparent 100%)",
                    }}
                  >
                    <div className="marquee-up flex flex-col gap-2">
                      {[...PATIENT_STORIES, ...PATIENT_STORIES].map((story, i) => (
                        <article
                          key={i}
                          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl px-3 py-2.5 flex flex-col justify-center gap-1.5 hover:border-primary/30 transition-colors relative overflow-hidden flex-shrink-0 h-[104px]"
                        >
                          <div className="text-primary text-4xl font-serif absolute top-0 right-3 opacity-10 leading-none pointer-events-none select-none">
                            "
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full border-2 border-primary/40 flex-shrink-0 overflow-hidden">
                              <img
                                src={story.img}
                                alt={`Patient ${story.initials}`}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-extrabold tracking-wide leading-tight">
                                  {story.initials}
                                </span>
                                <MdVerified className="w-2.5 h-2.5 text-primary flex-shrink-0" />
                              </div>
                              <div className="text-[8px] text-foreground/60 uppercase tracking-[0.18em] font-bold mt-0.5 truncate">
                                {story.demo}
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }).map((_, s) => (
                                <Star key={s} className="w-2 h-2 fill-primary text-primary" />
                              ))}
                            </div>
                          </div>
                          <p className="text-foreground/80 font-medium leading-snug text-[11px] md:text-[12px] relative z-10 line-clamp-3">
                            "{story.quote}"
                          </p>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

        </main>

        {/* FOOTER — minimal */}
        <footer className="bg-card border-t border-white/5 pt-14 pb-8">
          <div className="container mx-auto px-6 max-w-6xl">
            {/* Brand row */}
            <div className="flex flex-col items-center text-center mb-10">
              <img src={lightLogo} alt="rememore" className="h-7" />
            </div>

            {/* Action card buttons — linear rounded */}
            <div className="flex flex-wrap justify-center gap-2.5 mb-10">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
              >
                <FaWhatsapp className="w-3.5 h-3.5 text-green-400" />
                WhatsApp
              </a>
              <a
                href={PHONE_LINK}
                className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
              >
                <Phone className="w-3.5 h-3.5 text-primary" />
                Call
              </a>
              <a
                href={EMAIL_LINK}
                className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
              >
                <Mail className="w-3.5 h-3.5 text-primary" />
                Email
              </a>
              <a
                href={GOOGLE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
              >
                <FcGoogle className="w-3.5 h-3.5" />
                Google
              </a>
              <a
                href={TRUSTPILOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/40 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 hover:text-white transition-all"
              >
                <SiTrustpilot className="w-3.5 h-3.5 text-green-400" />
                Trustpilot
              </a>
            </div>

            {/* Section nav — minimal underline links */}
            <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 mb-10">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-[11px] uppercase tracking-[0.25em] font-bold text-foreground/55 hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Bottom spacer — preserves bottom margin so the sticky-bar fade has clean breathing room */}
            <div className="md:hidden h-24" aria-hidden></div>
            <p className="text-[11px] text-center text-[#646F79] my-5">This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.</p>
          </div>
        </footer>

        {/* Floating WhatsApp Bubble (Desktop) */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex fixed bottom-8 right-8 w-16 h-16 rounded-full items-center justify-center text-white shadow-[0_0_30px_rgba(34,197,94,0.5)] hover:scale-110 transition-transform z-50 bg-[#218f62]"
          aria-label="Chat on WhatsApp"
        >
          <FaWhatsapp className="w-8 h-8" />
        </a>

        {/* Mobile sticky-bar backdrop fade — sits behind the sticky bar so page content blurs into the bar */}
        <div
          aria-hidden
          className={`md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none h-32 transition-opacity duration-300 ${
            isNavOpen ? "opacity-0" : "opacity-100"
          }`}
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 35%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage:
              "linear-gradient(to top, black 0%, black 60%, transparent 100%)",
            maskImage:
              "linear-gradient(to top, black 0%, black 60%, transparent 100%)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        />

        {/* Mobile Sticky Action Bar — hidden when mobile nav is open */}
        <div
          className={`md:hidden fixed left-0 right-0 z-50 px-3 pointer-events-none transition-all duration-300 ${
            isNavOpen ? "opacity-0 translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"
          }`}
          style={{ bottom: "max(env(safe-area-inset-bottom), 12px)" }}
          aria-hidden={isNavOpen}
        >
          <div className="relative pointer-events-auto bg-white/[0.06] backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-between p-1 shadow-[0_10px_40px_rgba(0,0,0,0.45)] overflow-hidden">
            {/* Glass top highlight */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent"></div>
            {/* Subtle inner glow */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.10),inset_0_-1px_0_rgba(0,0,0,0.20)]"></div>

            <a
              href={PHONE_LINK}
              className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-foreground/85 hover:text-white"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-wider font-bold">
                Call
              </span>
            </a>
            <div className="w-px h-5 bg-white/10"></div>
            <a
              href={EMAIL_LINK}
              className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-foreground/85 hover:text-white"
            >
              <Mail className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase tracking-wider font-bold">
                Email
              </span>
            </a>
            <div className="w-px h-5 bg-white/10"></div>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex-1 flex items-center justify-center gap-1.5 py-2 text-green-400 hover:text-green-300"
            >
              <FaWhatsapp className="w-4 h-4" />
              <span className="green-shimmer text-[10px] uppercase tracking-wider font-bold">
                WhatsApp
              </span>
            </a>
          </div>
        </div>

        {/* Consultation Form Popup */}
        <ConsultationDialog
          open={formOpen}
          onOpenChange={(o) => {
            setFormOpen(o);
            if (!o) setTimeout(() => setFormSuccess(false), 400);
          }}
          form={form}
          onSubmit={onSubmit}
          formSuccess={formSuccess}
          resetForm={() => {
            form.reset();
            setFormSuccess(false);
          }}
          onPrivacyClick={() => setPrivacyOpen(true)}
        />

        {/* Privacy Notice Popup */}
        <PrivacyDialog
          open={privacyOpen}
          onOpenChange={setPrivacyOpen}
          onAccept={() =>
            form.setValue("consent", true, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            })
          }
        />

      </div>
    </GoogleReCaptchaProvider>
  );
}

/* ----------------- Why Us — YouTube hover-mute / click-unmute ----------------- */

const WHYUS_VIDEO_ID = "gUFbMEXQuzQ";

function WhyUsVideoCard() {
  const [openFull, setOpenFull] = useState(false);
  const fullSrc = `https://www.youtube.com/embed/${WHYUS_VIDEO_ID}?autoplay=1&controls=1&modestbranding=1&rel=0`;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpenFull(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpenFull(true);
          }
        }}
        className="group relative w-full h-full min-h-[260px] sm:min-h-[420px] rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/50 shadow-[0_30px_80px_rgba(0,0,0,0.45)] hover:border-primary/40 transition-colors"
      >
        {/* Cover image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={whyUsCover}
            alt="Memorial Hospitals — patient stories"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        {/* Soft vignette so the play badge stays legible */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.25),_rgba(0,0,0,0.05)_55%,_transparent_75%)] pointer-events-none"></div>

        {/* Play badge */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="relative flex items-center justify-center w-14 h-14 md:w-20 md:h-20 rounded-full bg-primary/95 text-primary-foreground shadow-[0_0_40px_rgba(209,190,138,0.6)] transition-transform duration-300 group-hover:scale-110">
            <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping"></span>
            <Play className="relative w-5 h-5 md:w-7 md:h-7 fill-current ml-0.5" />
          </span>
        </div>
      </div>

      <Dialog open={openFull} onOpenChange={setOpenFull}>
        <DialogContent className="w-[min(96vw,960px)] max-w-[960px] border-white/10 bg-[#0a0a0a] p-0 overflow-hidden rounded-2xl">
          <DialogTitle className="sr-only">Memorial Hospitals — Patient Stories</DialogTitle>
          <DialogDescription className="sr-only">
            Video introduction by Memorial Hospitals about its plastic surgery and aesthetic services in Turkey.
          </DialogDescription>
          <div className="relative w-full aspect-video bg-black">
            {openFull && (
              <iframe
                src={fullSrc}
                title="Memorial Hospitals"
                className="absolute inset-0 w-full h-full"
                frameBorder={0}
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ----------------- (legacy doctor card removed) ----------------- */


function CountUp({
  end,
  duration = 1800,
  suffix = "",
  decimals = 0,
  formatThousands = false,
}: {
  end: number;
  duration?: number;
  suffix?: string;
  decimals?: number;
  formatThousands?: boolean;
}) {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setValue(end);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);

  const display = formatThousands
    ? Math.round(value).toLocaleString("en-US")
    : value.toFixed(decimals);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

/* ----------------- Journey / Process Section ----------------- */

const JOURNEY_PHASES = [
  {
    num: "01",
    Icon: ClipboardCheck,
    title: "Private Consultation",
    desc: "Encrypted, judgement-free assessment with our lead surgeon to map your private goals.",
    stat: "30",
    sub: "Min",
  },
  {
    num: "02",
    Icon: PenSquare,
    title: "Bespoke Plan",
    desc: "A surgical plan engineered to your anatomy — with transparent fixed pricing, no surprises.",
    stat: "48",
    sub: "Hrs",
  },
  {
    num: "03",
    Icon: Plane,
    title: "Transfer Support",
    desc: "5-star hotel, private chauffeur, and a dedicated coordinator from the moment you land.",
    stat: "Day 1",
    sub: "Transfer",
  },
  {
    num: "04",
    Icon: Activity,
    title: "Surgical Excellence",
    desc: "JCI-accredited theatre, board-certified team, and 24/7 elite recovery nursing care.",
    stat: "1-3",
    sub: "Hrs",
  },
  {
    num: "05",
    Icon: CalendarClock,
    title: "Lifetime Aftercare",
    desc: "We stand by every result for life. Free check-ins and free revisions if ever required.",
    stat: "∞",
    sub: "Forever",
  },
];

/* ----------------- Patient Stories ----------------- */

const PATIENT_STORIES = [
  {
    img: patientMan1,
    initials: "L.B.",
    demo: "Germany · Age 41",
    quote:
      "From the first WhatsApp message to the last follow-up, every step felt handled. Result speaks for itself.",
  },
  {
    img: patientWomanA,
    initials: "S.K.",
    demo: "United Kingdom · Age 38",
    quote:
      "I felt safe the entire time. Multilingual nurses, private suite, and a surgeon who actually listened to me.",
  },
  {
    img: patientMan2,
    initials: "T.H.",
    demo: "Netherlands · Age 46",
    quote:
      "Hair restoration changed how I show up at work. Natural density, zero giveaway, two weeks back to normal.",
  },
  {
    img: patientPA,
    initials: "P.A.",
    demo: "Spain · Age 35",
    quote:
      "Private suite, multilingual nurses, chef-prepared meals — every small detail was thought through.",
  },
  {
    img: patientMan3,
    initials: "J.M.",
    demo: "Ireland · Age 49",
    quote:
      "Less than half the price of two quotes I had at home — and frankly, with more experienced surgeons.",
  },
  {
    img: patientWomanB,
    initials: "C.D.",
    demo: "France · Age 43",
    quote:
      "Three weeks post-op and the only sign anything happened is the result itself. Discreet healing, dramatic outcome.",
  },
  {
    img: patientFS,
    initials: "F.S.",
    demo: "Austria · Age 36",
    quote:
      "Their psychologist consultation before surgery settled my nerves entirely — I went into the OR calm and clear.",
  },
  {
    img: patientMR,
    initials: "M.R.",
    demo: "Italy · Age 39",
    quote:
      "Months of research finally paid off. Surgical precision and follow-up care set a benchmark I didn't know existed.",
  },
  {
    img: patientVP,
    initials: "V.P.",
    demo: "Poland · Age 41",
    quote:
      "The 3D pre-op simulation showed me exactly what to expect. Reality matched the plan within millimetres.",
  },
  {
    img: patientGV,
    initials: "G.V.",
    demo: "Belgium · Age 44",
    quote:
      "Treated like guests at a five-star resort, with medical care held to the very same standard.",
  },
];

/* ----------------- Sub-components ----------------- */

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  divider = false,
  uppercase = false,
  align = "center",
  titleClassName,
  subtitleClassName,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: string;
  divider?: boolean;
  uppercase?: boolean;
  align?: "center" | "left";
  titleClassName?: string;
  subtitleClassName?: string;
}) {
  const isLeft = align === "left";
  return (
    <div className={isLeft ? "text-left max-w-2xl" : "text-center max-w-3xl mx-auto"}>
      <div className="inline-block border border-white/10 rounded-full px-4 py-1.5 bg-white/[0.03] backdrop-blur-sm mb-4 md:mb-6">
        <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-primary font-bold">
          {eyebrow}
        </span>
      </div>
      <h2
        className={`font-extrabold leading-[1.05] tracking-tight mb-3 md:mb-5 ${
          uppercase ? "uppercase tracking-[0.02em]" : ""
        } ${titleClassName ?? "text-3xl md:text-5xl lg:text-6xl"}`}
      >
        {title}
      </h2>
      {divider && (
        <div className={`${isLeft ? "" : "mx-auto"} w-16 h-[3px] rounded-full bg-primary mb-6`}></div>
      )}
      {subtitle && (
        <p className={`text-foreground/75 font-medium leading-relaxed ${subtitleClassName ?? "text-xs md:text-base"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function ConsultationCard({
  form,
  onSubmit,
  formSuccess,
  resetForm,
  onPrivacyClick,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
  onSubmit: (v: z.infer<typeof formSchema>) => void;
  formSuccess: boolean;
  resetForm: () => void;
  onPrivacyClick?: () => void;
}) {
  return (
    <div className="bg-transparent relative">
      <div className="relative z-10">
        {formSuccess ? (
          <SuccessScreen onReset={resetForm} />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-[0.25em] text-primary font-bold">
                Private & Confidential
              </span>
            </div>
            <h3 className="font-extrabold text-2xl md:text-3xl mb-1.5 tracking-tight">
              Request a <span className="text-primary">Consultation</span>
            </h3>
            <p className="text-sm text-foreground/65 mb-5 font-medium text-balance">
              A senior advisor will respond personally within 24h.
            </p>

            <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-3"
                    >
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Full Name"
                                className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Email Address"
                                type="email"
                                className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Phone: country code + number, no separate country field */}
                      <div className="grid grid-cols-[140px_1fr] gap-3">
                        <FormField
                          control={form.control}
                          name="dialCode"
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="bg-black/25 border-white/10 h-11 rounded-xl focus:ring-primary/50 text-base font-medium text-white">
                                    <SelectValue placeholder="Code" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="bg-card border-white/10 rounded-xl max-h-72">
                                  {COUNTRY_CODES.map((c) => (
                                    <SelectItem
                                      key={c.code}
                                      value={c.dial + ":" + c.code}
                                      className="font-medium"
                                    >
                                      <span className="inline-flex items-center gap-2 font-medium">
                                        <span className="text-base leading-none">
                                          {c.flag}
                                        </span>
                                        <span className="text-white">
                                          {c.iso}
                                        </span>
                                        <span className="text-white">
                                          {c.dial}
                                        </span>
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  placeholder="Phone Number"
                                  type="tel"
                                  inputMode="tel"
                                  className="bg-black/25 border-white/10 h-11 rounded-xl focus-visible:ring-primary/50 text-base font-medium"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="consent"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-2.5 space-y-0 pt-0.5">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                className="border-white/25 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground mt-0.5 rounded"
                              />
                            </FormControl>
                            <div className="space-y-0 leading-none">
                              <FormLabel className="text-[10px] text-foreground/65 font-medium leading-snug">
                                I agree to the{" "}
                                <button
                                  type="button"
                                  onClick={onPrivacyClick}
                                  className="font-bold text-primary hover:text-primary/80 underline-offset-2 hover:underline cursor-pointer"
                                >
                                  Privacy Notice
                                </button>{" "}
                                and consent to be contacted regarding my consultation request.
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />

                      <div className="pt-1 px-1 -mx-1">
                        <RecaptchaSubmitButton
                          isSubmitting={form.formState.isSubmitting}
                          onTokenReceived={(token) => {
                            form.handleSubmit((values) =>
                              onSubmit({
                                ...values,
                                recaptchaToken: token,
                              })
                            )();
                          }}
                        />
                      </div>
                      <p className="text-center text-[9px] text-foreground/45 mt-1.5 uppercase tracking-wider flex items-center justify-center gap-1 font-bold whitespace-nowrap">
                        <ShieldCheck className="w-3 h-3 flex-shrink-0" /> Encrypted · Never shared with third parties
                      </p>
                    </form>
                </Form>
          </>
        )}
      </div>
    </div>
  );
}

const RecaptchaSubmitButton = ({ onTokenReceived, isSubmitting }: { onTokenReceived: (token: string) => void, isSubmitting: boolean }) => { const { executeRecaptcha } = useGoogleReCaptcha(); const handleClick = async (e: React.MouseEvent) => { e.preventDefault(); try { if (!executeRecaptcha) { console.log("ReCAPTCHA henüz hazır değil"); return; } const token = await executeRecaptcha("appointment_form"); if (!token) { console.log("Token alınamadı"); return; } onTokenReceived(token); } catch (err) { console.error("Recaptcha error:", err); } }; return ( <Button type="button" onClick={handleClick} disabled={isSubmitting} className="w-full bg-primary ..." > {isSubmitting ? "Sending..." : "Book Appointment"} </Button> ); };

function SuccessScreen({ onReset }: { onReset: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="py-6 flex flex-col items-center justify-center text-center"
    >
      {/* Animated check */}
      <div className="relative mb-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/5 border border-primary/30 flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-[0_0_30px_rgba(200,165,110,0.5)]"
          >
            <Check className="w-8 h-8 text-primary-foreground" strokeWidth={3} />
          </motion.div>
        </motion.div>
        {/* Sparkles */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="absolute -top-2 -right-2"
        >
          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold mb-2">
          Request Received
        </div>
        <h4 className="text-2xl md:text-3xl font-extrabold mb-3 tracking-tight text-white">
          Welcome to <span className="text-white">rememore</span>
        </h4>
        <p className="text-sm text-foreground/75 font-medium leading-relaxed mb-2 max-w-sm">
          Your request has been securely transmitted. A senior patient
          coordinator will reach out personally within{" "}
          <strong className="text-foreground">24 hours</strong>.
        </p>
        <p className="text-xs text-foreground/55 font-medium mb-7">
          Please check your inbox (and spam folder) for our discreet
          confirmation email.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="w-full space-y-3"
      >
        <Button
          className="w-full bg-green-500 hover:bg-green-600 text-white h-12 rounded-xl font-bold tracking-wide uppercase text-xs shadow-[0_0_20px_rgba(34,197,94,0.35)]"
          onClick={() => window.open(WHATSAPP_LINK, "_blank")}
        >
          <FaWhatsapp className="mr-2 w-5 h-5" />
          Continue on WhatsApp Now
        </Button>
        <button
          onClick={onReset}
          className="text-xs text-foreground/50 hover:text-primary transition-colors font-bold uppercase tracking-widest"
        >
          Submit another request
        </button>
      </motion.div>
    </motion.div>
  );
}

/* ----------------- Press Strip (As Seen In) ----------------- */

const PRESS_LOGOS: { src: string; alt: string }[] = [
  { src: pressMemorial,        alt: "Memorial" },
  { src: pressWashTimes,       alt: "The Washington Times" },
  { src: pressYahoo,           alt: "Yahoo Finance" },
  { src: pressIBT,             alt: "International Business Times" },
  { src: pressLifeStyle,       alt: "Life & Style" },
  { src: pressNYWeekly,        alt: "New York Weekly" },
  { src: pressDailyCaller,     alt: "Daily Caller" },
  { src: pressTechBullion,     alt: "TechBullion" },
  { src: pressFirst,           alt: "First for Women" },
  { src: pressBenzinga,        alt: "Benzinga" },
  { src: pressMarketsInsider,  alt: "Markets Insider" },
  { src: pressFox8,            alt: "Fox 8" },
  { src: pressWikipedia,       alt: "Wikipedia" },
];

function PressStrip() {
  // Triple the logo set so the seamless wrap has plenty of headroom in either drag direction.
  const loop = [...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS];

  const trackRef = useRef<HTMLDivElement | null>(null);
  const setWidthRef = useRef(0); // width of ONE PRESS_LOGOS set inside the track
  const isHoverRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const movedRef = useRef(false);

  // Auto-scroll with rAF; seamless wrap-around in both directions.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      // total scrollable width = 3 sets, so one set width = scrollWidth / 3
      setWidthRef.current = el.scrollWidth / 3;
      // start in the middle so user can drag in either direction
      el.scrollLeft = setWidthRef.current;
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const speed = isMobile ? 0.6 : 0.35; // px per frame

    let rafId = 0;
    const tick = () => {
      const node = trackRef.current;
      if (node) {
        if (!isHoverRef.current && !isDraggingRef.current) {
          node.scrollLeft += speed;
        }
        // Seamless wrap — keep scrollLeft within the middle set range
        const w = setWidthRef.current;
        if (w > 0) {
          if (node.scrollLeft >= w * 2) node.scrollLeft -= w;
          else if (node.scrollLeft <= 0) node.scrollLeft += w;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    movedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = el.scrollLeft;
    el.setPointerCapture(e.pointerId);
    el.style.cursor = "grabbing";
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const el = trackRef.current;
    if (!el) return;
    const delta = e.clientX - dragStartXRef.current;
    if (Math.abs(delta) > 4) movedRef.current = true;
    el.scrollLeft = dragStartScrollRef.current - delta;
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    isDraggingRef.current = false;
    try { el.releasePointerCapture(e.pointerId); } catch {}
    el.style.cursor = "grab";
  };

  return (
    <section
      aria-label="Featured in"
      className="relative bg-background border-b border-white/5 overflow-hidden py-5 md:py-7"
    >
      {/* Edge fades on left/right for an elegant marquee falloff */}
      <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[#0a0a0a] to-transparent pointer-events-none z-[2]"></div>
      <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[#0a0a0a] to-transparent pointer-events-none z-[2]"></div>

      <div className="text-center text-[10px] md:text-[11px] font-extrabold tracking-[0.32em] uppercase text-foreground/45 mb-3 md:mb-3">
        As Featured In
      </div>

      <div
        ref={trackRef}
        className="press-drag-track relative w-full overflow-x-auto overflow-y-hidden"
        style={{ cursor: "grab" }}
        onMouseEnter={() => { isHoverRef.current = true; }}
        onMouseLeave={() => { isHoverRef.current = false; }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={(e) => {
          // Suppress click after a real drag so logos don't trigger anything they shouldn't
          if (movedRef.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ul className="flex items-center gap-12 md:gap-20 w-max select-none">
          {loop.map((logo, i) => (
            <li
              key={`${logo.alt}-${i}`}
              className="flex items-center justify-center h-14 md:h-20 shrink-0 opacity-60 hover:opacity-100 transition-opacity duration-500 ease-out"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                loading="lazy"
                draggable={false}
                className="h-full w-auto object-contain select-none pointer-events-none"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ProcedureCard({
  img,
  num,
  title,
  desc,
  features,
  openWhatsApp,
  openConsultation,
}: {
  img: string;
  num: string;
  title: string;
  desc: string;
  features: string[];
  openWhatsApp: () => void;
  openConsultation: () => void;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
      className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden flex flex-col md:flex-row hover:border-primary/40 transition-all duration-500 group hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative md:w-[44%] h-32 md:h-auto flex-shrink-0 overflow-hidden">
        <img
          src={img}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-card/60"></div>
      </div>

      {/* Content */}
      <div className="p-3 md:p-7 flex flex-col flex-grow">
        <h3 className="text-sm md:text-2xl font-extrabold mb-2 md:mb-3 leading-tight tracking-tight">
          {title}
        </h3>
        <p className="hidden md:block text-sm text-foreground/75 font-medium leading-relaxed mb-5">
          {desc}
        </p>

        <ul className="flex flex-col gap-1 md:gap-2 mb-2">
          {features.map((feat, i) => (
            <li
              key={i}
              className="flex items-center gap-1.5 text-[10px] md:text-xs font-bold text-foreground/85 capitalize whitespace-nowrap"
            >
              <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-primary flex-shrink-0"></div>
              {feat}
            </li>
          ))}
        </ul>

        {/* Single Book CTA — always visible on mobile; fades in on card hover for desktop only */}
        <div className="mt-3 md:mt-5 md:opacity-0 md:group-hover:opacity-100 md:transition-opacity md:duration-500">
          <Button
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-9 md:h-11 rounded-lg font-extrabold tracking-wide uppercase text-[11px] md:text-xs"
            onClick={openConsultation}
          >
            Book
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

function CertificateMedal() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 25;
    const y = -((e.clientY - rect.top) / rect.height - 0.5) * 25;
    setTilt({ x, y });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      className="relative w-full max-w-[100px] md:max-w-[200px] mx-auto aspect-square"
      style={{ perspective: "1200px" }}
      onMouseMove={onMouseMove}
      onMouseLeave={onLeave}
    >
      {/* Glow */}
      <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full"></div>

      <div
        className="relative w-full h-full transition-transform duration-300 ease-out group"
        style={{
          transform: `rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
          transformStyle: "preserve-3d",
        }}
      >
        {/* Outer ring */}
        <div
          className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent border border-primary/30"
          style={{ transform: "translateZ(20px)" }}
        ></div>

        {/* Medal */}
        <div
          className="absolute inset-6 rounded-full bg-gradient-to-br from-card to-secondary border border-white/15 flex items-center justify-center overflow-hidden"
          style={{ transform: "translateZ(40px)" }}
        >
          <img
            src={jciCertificate}
            alt="The Joint Commission – National Quality Approval"
            className="w-[120%] h-[120%] object-contain drop-shadow-[0_10px_25px_rgba(200,165,110,0.4)]"
          />

          {/* Shine sweep */}
          <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
            <div className="absolute -inset-y-4 -left-1/2 w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-0 group-hover:translate-x-[400%] transition-transform duration-1500 ease-out"></div>
          </div>
        </div>

      </div>
    </div>
  );
}

function FAQItem({ value, q, a }: { value: string; q: string; a: string }) {
  return (
    <AccordionItem
      value={value}
      className="border-b border-white/10 last:border-0"
    >
      <AccordionTrigger className="text-left font-bold text-sm md:text-lg hover:text-primary transition-colors hover:no-underline [&[data-state=open]]:text-primary py-3 md:py-5">
        {q}
      </AccordionTrigger>
      <AccordionContent className="text-foreground/75 font-medium leading-relaxed text-xs md:text-base pb-4 md:pb-5 pt-1">
        {a}
      </AccordionContent>
    </AccordionItem>
  );
}

/* ----------------- Personalized Plan Quiz Modal ----------------- */


/* ----------------- Consultation Dialog ----------------- */

function ConsultationDialog({
  open,
  onOpenChange,
  form,
  onSubmit,
  formSuccess,
  resetForm,
  onPrivacyClick,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  form: ReturnType<typeof useForm<z.infer<typeof formSchema>>>;
  onSubmit: (v: z.infer<typeof formSchema>) => void;
  formSuccess: boolean;
  resetForm: () => void;
  onPrivacyClick?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-transparent border-0 p-0 shadow-none max-h-[92vh] overflow-visible">
        <DialogTitle className="sr-only">Request a Consultation</DialogTitle>
        <DialogDescription className="sr-only">
          Private and confidential consultation request form.
        </DialogDescription>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
          <div className="shine-border relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-6 md:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.7)] max-h-[88vh] overflow-y-auto">
            <ConsultationCard
              form={form}
              onSubmit={onSubmit}
              formSuccess={formSuccess}
              resetForm={resetForm}
              onPrivacyClick={onPrivacyClick}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrivacyDialog({
  open,
  onOpenChange,
  onAccept,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onAccept?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-transparent border-0 p-0 shadow-none max-h-[92vh] overflow-visible">
        <DialogTitle className="sr-only">Privacy Notice</DialogTitle>
        <DialogDescription className="sr-only">
          How rememore handles personal information from consultation requests.
        </DialogDescription>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/5 to-transparent rounded-[3rem] blur-3xl pointer-events-none"></div>
          <div className="relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl border border-white/15 rounded-[2rem] p-5 md:p-7 shadow-[0_30px_80px_rgba(0,0,0,0.7)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
            <div className="pr-8">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-primary font-bold">
                  Confidentiality First
                </span>
              </div>
              <h3 className="font-extrabold text-xl md:text-2xl mb-2 tracking-tight leading-tight">
                Privacy <span className="text-primary">Notice</span>
              </h3>
              <p className="text-[11px] text-foreground/55 mb-4 font-medium uppercase tracking-wider">
                Last updated · April 2026
              </p>
            </div>

            <div className="space-y-4 text-[13px] md:text-sm text-foreground/80 font-medium leading-relaxed">
              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  1. What We Collect
                </h4>
                <p>
                  When you request a consultation we collect the information you choose to share with us:
                  full name, email address, phone number with country code, and any details you add about your goals.
                  We do not collect medical records at this stage.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  2. How We Use It
                </h4>
                <p>
                  Your information is used solely to respond personally to your request, schedule a private
                  consultation, and prepare a confidential treatment plan with Prof. Dr. Abdulmuttalip Şimşek
                  and the rememore team. We never use your data for unrelated marketing.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  3. Who Has Access
                </h4>
                <p>
                  Access is restricted to your dedicated senior advisor, the surgical team, and the Memorial
                  Hospitals coordination office. Your data is never sold, rented, or shared with third-party
                  marketers, advertisers, or social platforms.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  4. Storage &amp; Security
                </h4>
                <p>
                  Submissions are transmitted over encrypted (TLS) connections and stored in access-controlled
                  systems within the EU. Patient identifiers used in published testimonials are anonymized.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  5. Your Rights
                </h4>
                <p>
                  You may request access to, correction of, or deletion of your personal data at any time by
                  replying to any message from our team or by contacting us via WhatsApp. We will action verified
                  requests within 30 days.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  6. Cookies &amp; Analytics
                </h4>
                <p>
                  This website uses only essential cookies required for the form and video previews to function.
                  No advertising or cross-site tracking cookies are set.
                </p>
              </section>

              <section>
                <h4 className="text-primary font-bold uppercase tracking-wider text-[11px] mb-1.5">
                  7. Contact
                </h4>
                <p>
                  Questions about this notice? Reach the rememore privacy desk through the WhatsApp button at
                  the bottom of this page and a senior advisor will respond within 24 hours.
                </p>
              </section>
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 px-1 pb-1">
              <Button
                type="button"
                onClick={() => {
                  onAccept?.();
                  onOpenChange(false);
                }}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-xl font-extrabold tracking-[0.3em] uppercase text-xs md:text-sm shadow-[0_0_22px_rgba(200,165,110,0.32)] transition-all"
              >
                Got it · Accept
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- Personalized Plan Section ----------------- */


type Gender = "female" | "male";

const RECOMMENDATION_PROCEDURES: Record<string, { id: string; title: string; tag: string; sub: string }> = {
  facial:    { id: "facial",    title: "Facial Surgery",        tag: "Face",    sub: "Rhinoplasty · Facelift · Eyelid Lift" },
  hair:      { id: "hair",      title: "Hair Transplantation",  tag: "Hair",    sub: "DHI · Sapphire FUE · PRP" },
  dental:    { id: "dental",    title: "Dental Treatments",     tag: "Smile",   sub: "Hollywood Smile · Implants · Veneers" },
  breast:    { id: "breast",    title: "Breast Surgery",        tag: "Breast",  sub: "Augmentation · Lift · Reduction" },
  body:      { id: "body",      title: "Body Surgery",          tag: "Body",    sub: "Liposuction · BBL · Tummy Tuck" },
  bariatric: { id: "bariatric", title: "Bariatric Surgery",     tag: "Weight",  sub: "Gastric Sleeve · Bypass · Balloon" },
  arms:      { id: "arms",      title: "Arm Lift · Liposuction", tag: "Arms",   sub: "Brachioplasty · Targeted Lipo" },
  legs:      { id: "legs",      title: "Leg Aesthetics",        tag: "Legs",    sub: "Thigh Lift · Calf Shaping · Lipo" },
  glutes:    { id: "glutes",    title: "Brazilian Butt Lift",   tag: "Glutes",  sub: "BBL · Implants · Fat Transfer" },
  genital_f: { id: "genital_f", title: "Genital Aesthetics",    tag: "Genital", sub: "Vaginoplasty · Labiaplasty · Clitoroplasty" },
  genital_m: { id: "genital_m", title: "Genital Aesthetics",    tag: "Genital", sub: "Penile Aesthetics · Thickening · Enlargement" },
};

/* Body map — uses a real human silhouette image (230 x 500), padded into a
   wider viewBox so we have room for label pills on either side of the body.
   Each region defines an anatomical "highlight ellipse" (cx/cy/rx/ry) plus
   an external label position (labelX/labelY) connected with a thin line. */
const BODY_VIEWBOX_W = 380;
const BODY_VIEWBOX_H = 510;
const SIL_OFFSET_X = 75;   // silhouette image x offset inside the viewBox (centers a 230-wide silhouette in a 380 viewBox)
const SIL_W = 230;
const SIL_H = 500;

type BodyRegion = {
  id: keyof typeof RECOMMENDATION_PROCEDURES;
  label: string;
  cx: number; cy: number;     // anatomical center inside the viewBox
  rx: number; ry: number;     // highlight ellipse radii (shaped like the body part)
  labelX: number; labelY: number; // label pill center
  side: "left" | "right";     // which side the connector exits the body
};

/* Anatomy reference (silhouette image is 230x500, body horizontal center
   sits at image-x=114 → viewBox cx = 75 + 114 = 189 ≈ 190).
   Within the head: crown ~y=0-20, eyes ~y=20-32, nose ~y=30-38,
   mouth ~y=38-44, chin ~y=44-46, neck ~y=46-78, shoulders ~y=78+. */
const BODY_REGIONS: Record<Gender, BodyRegion[]> = {
  female: [
    { id: "hair",      label: "Hair",        cx: 190, cy: 10,  rx: 18, ry: 10, labelX: 48,  labelY: 14,  side: "left"  },
    { id: "facial",    label: "Face",        cx: 190, cy: 28,  rx: 22, ry: 14, labelX: 332, labelY: 32,  side: "right" },
    { id: "dental",    label: "Smile",       cx: 190, cy: 42,  rx: 11, ry: 4,  labelX: 48,  labelY: 56,  side: "left"  },
    { id: "breast",    label: "Breast",      cx: 190, cy: 140, rx: 32, ry: 20, labelX: 332, labelY: 138, side: "right" },
    { id: "arms",      label: "Arms",        cx: 112, cy: 200, rx: 12, ry: 38, labelX: 48,  labelY: 196, side: "left"  },
    { id: "bariatric", label: "Weight Loss", cx: 190, cy: 192, rx: 30, ry: 18, labelX: 332, labelY: 188, side: "right" },
    { id: "body",      label: "Tummy",       cx: 190, cy: 226, rx: 26, ry: 18, labelX: 48,  labelY: 232, side: "left"  },
    { id: "genital_f", label: "Genital",     cx: 190, cy: 268, rx: 14, ry: 10, labelX: 332, labelY: 270, side: "right" },
    { id: "glutes",    label: "Glutes",      cx: 190, cy: 300, rx: 34, ry: 18, labelX: 48,  labelY: 300, side: "left"  },
    { id: "legs",      label: "Legs",        cx: 190, cy: 395, rx: 38, ry: 60, labelX: 332, labelY: 395, side: "right" },
  ],
  male: [
    { id: "hair",      label: "Hair",        cx: 190, cy: 10,  rx: 18, ry: 10, labelX: 48,  labelY: 14,  side: "left"  },
    { id: "facial",    label: "Face",        cx: 190, cy: 28,  rx: 22, ry: 14, labelX: 332, labelY: 32,  side: "right" },
    { id: "dental",    label: "Smile",       cx: 190, cy: 42,  rx: 11, ry: 4,  labelX: 48,  labelY: 56,  side: "left"  },
    { id: "arms",      label: "Arms",        cx: 112, cy: 200, rx: 12, ry: 38, labelX: 48,  labelY: 196, side: "left"  },
    { id: "bariatric", label: "Weight Loss", cx: 190, cy: 192, rx: 30, ry: 18, labelX: 332, labelY: 188, side: "right" },
    { id: "body",      label: "Tummy",       cx: 190, cy: 226, rx: 26, ry: 18, labelX: 48,  labelY: 232, side: "left"  },
    { id: "genital_m", label: "Genital",     cx: 190, cy: 268, rx: 14, ry: 10, labelX: 332, labelY: 270, side: "right" },
    { id: "legs",      label: "Legs",        cx: 190, cy: 395, rx: 38, ry: 60, labelX: 332, labelY: 395, side: "right" },
  ],
};

function BodyMap({
  regions,
  selected,
  onSelect,
  idSuffix,
}: {
  regions: BodyRegion[];
  selected: Set<string>;
  onSelect: (id: string) => void;
  idSuffix: "f" | "m";
}) {
  const filterId = `bodyGoldTint-${idSuffix}`;
  const glowId = `bodyMapGlow-${idSuffix}`;
  const blurId = `bodyMapBlur-${idSuffix}`;
  const [hoverId, setHoverId] = useState<string | null>(null);

  // Approximate label pill width based on label length (in viewBox units).
  const labelWidth = (label: string) => Math.max(46, label.length * 6.5 + 16);

  return (
    <svg
      viewBox={`0 0 ${BODY_VIEWBOX_W} ${BODY_VIEWBOX_H}`}
      className="w-full h-auto max-h-[560px] mx-auto"
      role="group"
      aria-label="Body region selector"
    >
      <defs>
        {/* Tint the (originally black) silhouette to a soft translucent gold */}
        <filter id={filterId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.82
                    0 0 0 0 0.74
                    0 0 0 0 0.54
                    0 0 0 0.78 0"
          />
        </filter>
        {/* Soft glow filter for highlight ellipses */}
        <filter id={blurId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" />
        </filter>
        <radialGradient id={glowId} cx="0.5" cy="0.5" r="0.6">
          <stop offset="0%" stopColor="rgba(209,190,138,0.18)" />
          <stop offset="100%" stopColor="rgba(209,190,138,0)" />
        </radialGradient>
      </defs>

      {/* Soft golden ambient glow behind the body */}
      <ellipse
        cx={BODY_VIEWBOX_W / 2}
        cy={BODY_VIEWBOX_H / 2}
        rx="120"
        ry="240"
        fill={`url(#${glowId})`}
      />

      {/* Real human silhouette, gold-tinted, centered in the wider viewBox */}
      <image
        href={bodySilhouette}
        x={SIL_OFFSET_X}
        y="0"
        width={SIL_W}
        height={SIL_H}
        preserveAspectRatio="xMidYMid meet"
        filter={`url(#${filterId})`}
      />

      {/* Region highlights + external label pills */}
      {regions.map((r) => {
        const on = selected.has(r.id);
        const hot = on || hoverId === r.id;
        const w = labelWidth(r.label);
        const labelLeft = r.labelX - w / 2;
        // Connector line goes from the silhouette edge of the highlight to the
        // inner edge of the label pill.
        const lineX1 = r.side === "left" ? r.cx - r.rx : r.cx + r.rx;
        const lineX2 = r.side === "left" ? labelLeft + w : labelLeft;
        return (
          <g
            key={r.id}
            onMouseEnter={() => setHoverId(r.id)}
            onMouseLeave={() => setHoverId((h) => (h === r.id ? null : h))}
            style={{ pointerEvents: "auto" }}
          >
            {/* Soft anatomical highlight (spotlight) — sized to the body part */}
            <ellipse
              cx={r.cx}
              cy={r.cy}
              rx={r.rx}
              ry={r.ry}
              fill={hot ? "rgba(209,190,138,0.55)" : "rgba(209,190,138,0.10)"}
              filter={`url(#${blurId})`}
              style={{
                mixBlendMode: "screen",
                transition: "fill 200ms ease",
                pointerEvents: "none",
              }}
            />
            {/* Crisper inner ring on hover/select to make the area feel "lit" */}
            <ellipse
              cx={r.cx}
              cy={r.cy}
              rx={r.rx}
              ry={r.ry}
              fill="none"
              stroke={hot ? "rgba(209,190,138,0.9)" : "rgba(209,190,138,0)"}
              strokeWidth={hot ? 1.2 : 0}
              style={{
                strokeDasharray: "3 3",
                transition: "all 200ms ease",
                pointerEvents: "none",
              }}
            />

            {/* Connector line from body region to label pill */}
            <line
              x1={lineX1}
              y1={r.cy}
              x2={lineX2}
              y2={r.labelY}
              stroke={hot ? "rgba(209,190,138,0.95)" : "rgba(209,190,138,0.45)"}
              strokeWidth={hot ? 1 : 0.7}
              strokeDasharray="2.5 2.5"
              style={{ pointerEvents: "none" }}
            />
            {/* Tiny anchor dot at the body end of the connector */}
            <circle
              cx={lineX1}
              cy={r.cy}
              r={hot ? 2.2 : 1.6}
              fill={hot ? "#d1be8a" : "rgba(209,190,138,0.7)"}
              style={{ pointerEvents: "none" }}
            />

            {/* Invisible hit area on the body region — kept exactly to the
                highlight ellipse to avoid overlap between tightly clustered
                head regions (Hair / Face / Smile). */}
            <ellipse
              cx={r.cx}
              cy={r.cy}
              rx={r.rx}
              ry={r.ry}
              fill="rgba(0,0,0,0)"
              onClick={() => onSelect(r.id)}
              style={{ cursor: "pointer", pointerEvents: "all" }}
              role="button"
              aria-label={`Select ${r.label} area`}
              aria-pressed={on}
            />

            {/* Label pill (clickable) */}
            <rect
              x={labelLeft}
              y={r.labelY - 9}
              width={w}
              height={18}
              rx="9"
              ry="9"
              fill={
                on
                  ? "rgba(209,190,138,0.95)"
                  : hot
                  ? "rgba(209,190,138,0.18)"
                  : "rgba(15,15,18,0.78)"
              }
              stroke="rgba(209,190,138,0.85)"
              strokeWidth={on ? 1.4 : 0.9}
              onClick={() => onSelect(r.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(r.id);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`${r.label}`}
              aria-pressed={on}
              style={{
                filter: on
                  ? "drop-shadow(0 0 10px rgba(209,190,138,0.85))"
                  : "drop-shadow(0 1px 3px rgba(0,0,0,0.5))",
                transition: "all 200ms ease",
                cursor: "pointer",
                pointerEvents: "all",
                outline: "none",
              }}
            />
            <text
              x={r.labelX}
              y={r.labelY + 3}
              textAnchor="middle"
              fontSize="8"
              fontWeight="800"
              fill={on ? "#0d0d10" : "rgba(255,255,255,0.95)"}
              style={{
                pointerEvents: "none",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                userSelect: "none",
              }}
            >
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function FemaleBodySvg({ selected, onSelect }: { selected: Set<string>; onSelect: (id: string) => void }) {
  return <BodyMap regions={BODY_REGIONS.female} selected={selected} onSelect={onSelect} idSuffix="f" />;
}

function MaleBodySvg({ selected, onSelect }: { selected: Set<string>; onSelect: (id: string) => void }) {
  return <BodyMap regions={BODY_REGIONS.male} selected={selected} onSelect={onSelect} idSuffix="m" />;
}


function PersonalizedPlanSection({
  onBook,
  onWhatsApp,
}: {
  onBook: () => void;
  onWhatsApp: () => void;
}) {
  type Step = 0 | 1 | 2 | 3 | 4;
  const [step, setStep] = useState<Step>(0);
  const [gender, setGender] = useState<Gender | null>(null);
  const [age, setAge] = useState<number>(35);
  const [height, setHeight] = useState<number>(170);
  const [weight, setWeight] = useState<number>(70);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const totalSteps = 5;
  const progress = ((step + 1) / totalSteps) * 100;

  const toggleRegion = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const recommendations = Array.from(selected)
    .map((id) => RECOMMENDATION_PROCEDURES[id])
    .filter(Boolean);

  const canNextFromBody = selected.size > 0;

  const reset = () => {
    setStep(0);
    setGender(null);
    setAge(35);
    setHeight(170);
    setWeight(70);
    setSelected(new Set());
  };

  return (
    <div className="relative pt-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(209,190,138,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(209,190,138,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      <div className="relative">
        <SectionHeader
          eyebrow="04 — Your Personal Plan"
          title={
            <>
              Build Your <span className="text-primary">Treatment Plan</span>
              <br />
              in 60 Seconds
            </>
          }
          subtitle="Tap the area you want to improve — we'll match you with the right procedures."
        />
        <div className="mb-8" />

        <div className="max-w-4xl mx-auto bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-xl border border-white/12 rounded-3xl p-4 md:p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>

          {/* Progress */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/70">
                Step {step + 1} of {totalSteps}
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-primary">
              {Math.round(progress)}% Done
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-6">
            <motion.div
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-primary/70 to-primary"
              style={{ boxShadow: "0 0 12px rgba(209,190,138,0.6)" }}
            />
          </div>

          {/* Step 0 — Welcome */}
          {step === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="text-center py-6"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/15 border border-primary/40 mb-5"
                style={{ boxShadow: "0 0 24px rgba(209,190,138,0.45)" }}
              >
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-3">
                Welcome to your <span className="text-primary">personal plan</span>
              </h3>
              <p className="text-sm md:text-base text-foreground/75 font-medium max-w-xl mx-auto mb-6 leading-relaxed">
                Six quick steps. Tell us a bit about you, point at the body area you want to refine, and a senior advisor will follow up within 24 hours — privately.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-6 text-[10px] uppercase tracking-[0.2em] text-foreground/60 font-bold">
                <span className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-primary" /> 100% Private</span>
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-primary" /> JCI-Accredited</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-primary" /> No Spam</span>
              </div>
              <Button
                onClick={() => setStep(1)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-xl font-extrabold tracking-wide uppercase text-xs"
                style={{ boxShadow: "0 0 25px rgba(209,190,138,0.45)" }}
              >
                Start My Plan
              </Button>
            </motion.div>
          )}

          {/* Step 1 — Profile */}
          {step === 1 && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <h3 className="text-lg md:text-xl font-extrabold tracking-tight mb-5">
                A little about <span className="text-primary">you</span>
              </h3>

              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/65 mb-2">Gender</div>
                <div className="grid grid-cols-2 gap-3">
                  {(["female", "male"] as Gender[]).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGender(g)}
                      className={`h-14 rounded-xl border text-sm font-extrabold uppercase tracking-wide transition-all ${
                        gender === g
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-white/[0.04] border-white/10 hover:border-primary/40 hover:bg-white/[0.08]"
                      }`}
                      style={gender === g ? { boxShadow: "0 0 18px rgba(209,190,138,0.45)" } : undefined}
                    >
                      {g === "female" ? "Female" : "Male"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: "Age", value: age, setValue: setAge, min: 18, max: 75, unit: "yrs" },
                  { label: "Height", value: height, setValue: setHeight, min: 140, max: 210, unit: "cm" },
                  { label: "Weight", value: weight, setValue: setWeight, min: 40, max: 180, unit: "kg" },
                ].map((f) => (
                  <div key={f.label} className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-foreground/60 font-bold">{f.label}</span>
                      <span className="text-2xl font-extrabold text-primary tracking-tight">
                        {f.value}
                        <span className="text-xs text-foreground/60 ml-1 font-bold">{f.unit}</span>
                      </span>
                    </div>
                    <Slider
                      value={[f.value]}
                      onValueChange={(v) => f.setValue(v[0])}
                      min={f.min}
                      max={f.max}
                      step={1}
                      aria-label={f.label}
                      className="my-1"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(0)} className="border-white/15 hover:bg-white/5 h-11 px-5 rounded-xl font-extrabold tracking-wide uppercase text-[11px] bg-white/[0.03]">
                  Back
                </Button>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!gender}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-xl font-extrabold tracking-wide uppercase text-[11px] disabled:opacity-50"
                  style={gender ? { boxShadow: "0 0 18px rgba(209,190,138,0.4)" } : undefined}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2 — Body Map */}
          {step === 2 && (
            <motion.div
              key="bodymap"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <h3 className="text-lg md:text-xl font-extrabold tracking-tight mb-1">
                Tap what you'd like to <span className="text-primary">improve</span>
              </h3>
              <p className="text-xs text-foreground/65 mb-5 font-medium">
                Pick one or more areas — your selections glow in gold.
              </p>

              <div className="grid md:grid-cols-2 gap-5 items-center">
                <div className="bg-black/30 border border-white/10 rounded-2xl p-3 md:p-4">
                  {gender === "male" ? (
                    <MaleBodySvg selected={selected} onSelect={toggleRegion} />
                  ) : (
                    <FemaleBodySvg selected={selected} onSelect={toggleRegion} />
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/65 mb-3">
                    Selected · {selected.size}
                  </div>
                  {selected.size === 0 ? (
                    <div className="text-sm text-foreground/55 font-medium bg-white/[0.04] border border-white/10 border-dashed rounded-xl px-4 py-6 text-center">
                      Nothing selected yet — tap a glowing area on the body.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {recommendations.map((r) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between bg-white/[0.05] border border-primary/25 rounded-xl px-3 py-2.5"
                          style={{ boxShadow: "inset 0 0 12px rgba(209,190,138,0.08)" }}
                        >
                          <div>
                            <div className="text-xs font-extrabold">{r.title}</div>
                            <div className="text-[10px] text-foreground/55 font-medium">{r.sub}</div>
                          </div>
                          <button
                            onClick={() => toggleRegion(r.id)}
                            className="text-[10px] uppercase tracking-wider font-bold text-foreground/60 hover:text-primary"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="border-white/15 hover:bg-white/5 h-11 px-5 rounded-xl font-extrabold tracking-wide uppercase text-[11px] bg-white/[0.03]">
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canNextFromBody}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-xl font-extrabold tracking-wide uppercase text-[11px] disabled:opacity-50"
                  style={canNextFromBody ? { boxShadow: "0 0 18px rgba(209,190,138,0.4)" } : undefined}
                >
                  See Recommendations
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3 — Recommendations */}
          {step === 3 && (
            <motion.div
              key="reco"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <h3 className="text-lg md:text-xl font-extrabold tracking-tight mb-1">
                Your <span className="text-primary">matched</span> procedures
              </h3>
              <p className="text-xs text-foreground/65 mb-5 font-medium">
                Senior surgeons across {recommendations.length} treatment area{recommendations.length === 1 ? "" : "s"} — performed inside JCI-accredited Memorial Hospitals.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {recommendations.map((r) => (
                  <div
                    key={r.id}
                    className="bg-gradient-to-br from-primary/10 via-white/[0.04] to-white/[0.02] border border-primary/30 rounded-xl p-4"
                    style={{ boxShadow: "0 0 22px rgba(209,190,138,0.18)" }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[9px] uppercase tracking-[0.25em] font-extrabold text-primary">{r.tag}</span>
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-sm font-extrabold mb-1">{r.title}</div>
                    <div className="text-[11px] text-foreground/70 font-medium leading-relaxed">{r.sub}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 mt-5 text-[11px] text-foreground/65 font-medium leading-relaxed">
                Final personalized plan — including your suitable surgeon, hospital and stay length — is shared in your private consultation.
              </div>

              <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(2)} className="border-white/15 hover:bg-white/5 h-11 px-5 rounded-xl font-extrabold tracking-wide uppercase text-[11px] bg-white/[0.03]">
                  Edit Selection
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-xl font-extrabold tracking-wide uppercase text-[11px]"
                  style={{ boxShadow: "0 0 18px rgba(209,190,138,0.4)" }}
                >
                  Get My Plan
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4 — Final CTA → opens the standard consultation form popup */}
          {step === 4 && (
            <motion.div
              key="cta"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="text-center"
            >
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/15 border border-primary/50 mb-5"
                style={{ boxShadow: "0 0 30px rgba(209,190,138,0.55)" }}
              >
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl md:text-2xl font-extrabold tracking-tight mb-2">
                Your plan is <span className="text-primary">ready</span>
              </h3>
              <p className="text-sm text-foreground/70 font-medium max-w-md mx-auto mb-6 leading-relaxed">
                Open your private consultation form and a senior advisor will follow up personally within 24 hours with your fully personalized treatment plan.
              </p>

              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 mb-6 text-[11px] text-foreground/65 font-medium leading-relaxed max-w-md mx-auto flex items-start gap-2 text-left">
                <Lock className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                Encrypted &amp; private — your details are used only to share your plan and never sold.
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="border-white/15 hover:bg-white/5 h-12 px-5 rounded-xl font-extrabold tracking-wide uppercase text-[11px] bg-white/[0.03] w-full sm:w-auto"
                >
                  Back
                </Button>
                <Button
                  onClick={onBook}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 rounded-xl font-extrabold tracking-[0.2em] uppercase text-[12px] w-full sm:w-auto"
                  style={{ boxShadow: "0 0 22px rgba(209,190,138,0.45)" }}
                >
                  Get My Plan
                </Button>
                <Button
                  onClick={onWhatsApp}
                  variant="outline"
                  className="border-[#25D366]/40 hover:bg-[#25D366]/10 h-12 px-5 rounded-xl font-extrabold tracking-wide uppercase text-[11px] text-[#25D366] w-full sm:w-auto"
                >
                  <FaWhatsapp className="mr-2 w-4 h-4" />
                  WhatsApp
                </Button>
              </div>

              <button
                type="button"
                onClick={reset}
                className="mt-5 text-[10px] uppercase tracking-[0.25em] font-extrabold text-foreground/45 hover:text-foreground/70 transition-colors"
              >
                Build Another Plan
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------- Before / After Gallery ----------------- */

const BEFORE_AFTER_IMAGES: { src: string; label: string; country: string }[] = [
  { src: "https://cdn.rememore.com/lp/images/before-after/1-breast-augmentation-ba-united-states.jpg", label: "Breast Augmentation", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/9-breast-augmentation-ba-united-kingdom.jpg", label: "Breast Augmentation", country: "United Kingdom" },
  { src: "https://cdn.rememore.com/lp/images/before-after/10-breast-augmentation-ba-united-states.jpg", label: "Breast Augmentation", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/1-facelift-necklift-ba-germany.jpg", label: "Facelift / Necklift", country: "Germany" },
  { src: "https://cdn.rememore.com/lp/images/before-after/2-facelift-necklift-ba-united-states.jpg", label: "Facelift / Necklift", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/2-rhinoplasty-surgery-ba-united-states.jpg", label: "Rhinoplasty", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/3-rhinoplasty-surgery-ba-germany.jpg", label: "Rhinoplasty", country: "Germany" },
  { src: "https://cdn.rememore.com/lp/images/before-after/3-brazilian-butt-lift-ba-france.jpg", label: "Brazilian Butt Lift", country: "France" },
  { src: "https://cdn.rememore.com/lp/images/before-after/6-brazilian-butt-lift-ba-united-states.jpg", label: "Brazilian Butt Lift", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/4-mommy-makeover-ba-united-states.jpg", label: "Mommy Makeover", country: "United States" },
  { src: "https://cdn.rememore.com/lp/images/before-after/5-mommy-makeover-ba-united-kingdom.jpg", label: "Mommy Makeover", country: "United Kingdom" },
  { src: "https://cdn.rememore.com/lp/images/before-after/7-tummy-tuck-ba-united-states.jpg", label: "Tummy Tuck", country: "United States" },
];

function BeforeAfterGallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isOpen = openIndex !== null;
  const current = isOpen ? BEFORE_AFTER_IMAGES[openIndex] : null;

  const goPrev = useCallback(
    () =>
      setOpenIndex((i) =>
        i === null ? i : (i - 1 + BEFORE_AFTER_IMAGES.length) % BEFORE_AFTER_IMAGES.length
      ),
    []
  );
  const goNext = useCallback(
    () =>
      setOpenIndex((i) =>
        i === null ? i : (i + 1) % BEFORE_AFTER_IMAGES.length
      ),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") setOpenIndex(null);
    };
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, goPrev, goNext]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 max-w-7xl mx-auto mt-10 md:mt-14">
        {BEFORE_AFTER_IMAGES.map((it, i) => (
          <motion.button
            type="button"
            key={it.src}
            onClick={() => setOpenIndex(i)}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
            className="group relative aspect-[3/4.2] rounded-xl overflow-hidden border border-white/10 bg-[#0d0d0d] hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/60 transition-colors cursor-zoom-in"
            aria-label={`View ${it.label} before and after — patient from ${it.country}`}
          >
            <img
              src={it.src}
              alt={`${it.label} — before and after, patient from ${it.country}`}
              loading="lazy"
              className="absolute left-0 right-0 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              style={{ top: "-3px", height: "calc(100% + 3px)" }}
            />
          </motion.button>
        ))}
      </div>

      {createPortal(
      <AnimatePresence>
        {isOpen && current ? (
          <motion.div
            key="ba-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[2147483000] flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-label={`${current.label} — patient from ${current.country}`}
            onClick={() => setOpenIndex(null)}
          >
            {/* Blurred backdrop using the current image itself */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${current.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(40px) brightness(0.35) saturate(1.1)",
                transform: "scale(1.15)",
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-black/55 backdrop-blur-2xl"
              aria-hidden="true"
            />

            {/* Close button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenIndex(null);
              }}
              className="absolute top-4 right-4 md:top-6 md:right-6 z-10 h-11 w-11 rounded-full bg-black/55 hover:bg-black/80 border border-white/15 text-white/90 hover:text-white flex items-center justify-center backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/70"
              aria-label="Close gallery"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Prev / Next */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-black/55 hover:bg-black/80 border border-white/15 text-white/90 hover:text-white flex items-center justify-center backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/70"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-black/55 hover:bg-black/80 border border-white/15 text-white/90 hover:text-white flex items-center justify-center backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/70"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Image stage */}
            <motion.div
              key={current.src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              className="relative z-[5] max-w-[min(96vw,1100px)] max-h-[88vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.7)] bg-black">
                <img
                  src={current.src}
                  alt={`${current.label} — before and after, patient from ${current.country}`}
                  className="block max-w-[min(96vw,1100px)] max-h-[78vh] w-auto h-auto object-contain"
                  style={{ marginTop: "-3px" }}
                />
              </div>
              <div className="mt-4 text-center">
                <div className="text-white text-base md:text-lg font-semibold tracking-wide">
                  {current.label}
                </div>
                <div className="text-white/65 text-xs md:text-sm mt-1">
                  Patient from {current.country} ·{" "}
                  <span className="text-white/45">
                    {openIndex! + 1} / {BEFORE_AFTER_IMAGES.length}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>,
      document.body
      )}
    </>
  );
}

/* ----------------- Video Testimonials Gallery ----------------- */

const VIDEO_TESTIMONIALS: { poster: string; src?: string; procedure: string }[] = [
  {
    poster: coverPlasticSurgeryUk,
    src: "https://player.vimeo.com/progressive_redirect/playback/1160156933/rendition/1080p/file.mp4%20%281080p%29.mp4?loc=external&signature=9244bae604bada2e6a92a0e0c48c526a4a4ad9cbf8c78a7dadb1926b53a7c0c6",
    procedure: "Plastic Surgery",
  },
  {
    poster: "https://cdn.rememore.com/lp/images/testimonials/27.jpg",
    src: "https://player.vimeo.com/progressive_redirect/playback/1038532495/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=b8dd597db655a9774774f0d9583a31bf358b0f9d63cb78a584db30335d2b2aaf",
    procedure: "Plastic Surgery",
  },
  {
    poster: testimonialDe,
    src: "https://player.vimeo.com/progressive_redirect/playback/1038539884/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=e9cc22fd75d7e0368b89e22ede5f23003c64c59a0df99214a68f173076e86263",
    procedure: "Rhinoplasty",
  },
  {
    poster: "https://cdn.rememore.com/lp/images/testimonials/26.jpg",
    src: "https://player.vimeo.com/progressive_redirect/playback/1192515249/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=8eb031597abb351da2439edc09c17ba7feb365099b547b6b34c0e962df729d82",
    procedure: "Plastic Surgery",
  },
  {
    poster: coverHairTransplantAu,
    src: "https://player.vimeo.com/progressive_redirect/playback/1163543682/rendition/1440p/file.mp4%20%281440p%29.mp4?loc=external&signature=e0d3f1be1716ecd36d9138f4085f85af35206332c8c9ee78fac71af257f35508",
    procedure: "Hair Transplant",
  },
  {
    poster: coverGastricSleeveUk,
    src: "https://player.vimeo.com/progressive_redirect/playback/1038536131/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=f0bf9f763b3e2e404f3cf8b67802730a9aa090c23b6d469ba8833c88c144fe79",
    procedure: "Gastric Sleeve",
  },
  {
    poster: coverMommyMakeoverDe,
    src: "https://player.vimeo.com/progressive_redirect/playback/1192508341/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=d1d2d9de219d54137cfe285fc58ddf64ccee03c9d26f8c40966c433a2b1f982c",
    procedure: "Mommy Makeover",
  },
  {
    poster: coverHairRhinoFr,
    src: "https://player.vimeo.com/progressive_redirect/playback/1192508723/rendition/720p/file.mp4%20%28720p%29.mp4?loc=external&signature=6bb422026eecbf6f3ccbe3bb7c993e1ebc27f31060176aeb4427e8e0d93d0538",
    procedure: "Hair Transplant & Rhinoplasty",
  },
];

function VideoTestimonialCard({
  t,
  onOpen,
}: {
  t: typeof VIDEO_TESTIMONIALS[number];
  onOpen: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = Boolean(t.src);
  const isYouTube = hasVideo && (t.src!.includes("youtube.com") || t.src!.includes("youtu.be"));

  const handleEnter = () => {
    if (!hasVideo) return;
    setMounted(true);
    setHovered(true);
  };
  const handleLeave = () => {
    setHovered(false);
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch { /* noop */ }
    }
  };

  useEffect(() => {
    if (!hovered || isYouTube) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [hovered, mounted, isYouTube]);

  return (
    <button
      type="button"
      onClick={hasVideo ? onOpen : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      className={`group relative w-full aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] hover:border-primary/40 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50 ${hasVideo ? "cursor-pointer" : "cursor-default"}`}
      aria-label={`Open ${t.procedure} patient story`}
    >
      <img
        src={t.poster}
        alt={`${t.procedure} — verified patient story`}
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-[1.03] ${hovered && mounted && hasVideo && !isYouTube ? "opacity-0" : "opacity-100"}`}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.background = "linear-gradient(135deg, #1a1a1a, #0d0d0d)";
        }}
      />
      {mounted && hasVideo && !isYouTube && (
        <video
          ref={videoRef}
          src={t.src}
          muted
          loop
          playsInline
          preload="metadata"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hovered ? "opacity-100" : "opacity-0"}`}
        />
      )}
      {mounted && hasVideo && isYouTube && hovered && (
        <iframe
          src={`${t.src}${t.src!.includes("?") ? "&" : "?"}autoplay=1&mute=1&controls=0&modestbranding=1&playsinline=1&loop=1`}
          title={`${t.procedure} — preview`}
          className="absolute inset-0 w-full h-full bg-black pointer-events-none"
          frameBorder={0}
          allow="autoplay; encrypted-media"
        />
      )}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.25),_rgba(0,0,0,0.05)_55%,_transparent_75%)] pointer-events-none" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span
          className={`relative flex items-center justify-center w-14 h-14 rounded-full bg-white/95 text-black transition-all duration-300 group-hover:scale-110 ${hovered ? "opacity-0" : "opacity-100"}`}
          style={{ boxShadow: "0 0 30px rgba(255,255,255,0.45)" }}
        >
          <span className="absolute inset-0 rounded-full bg-white/40 animate-ping"></span>
          <Play className="relative w-5 h-5 fill-current ml-0.5" />
        </span>
      </div>
    </button>
  );
}

function VideoTestimonialLightbox({
  items,
  index,
  onClose,
  onIndex,
}: {
  items: typeof VIDEO_TESTIMONIALS;
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      else if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, items.length, onClose, onIndex]);

  const t = items[index];
  const isYouTube = t.src && (t.src.includes("youtube.com") || t.src.includes("youtu.be"));
  const prev = () => onIndex((index - 1 + items.length) % items.length);
  const next = () => onIndex((index + 1) % items.length);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${t.procedure} patient story`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); prev(); }}
        aria-label="Previous video"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); next(); }}
        aria-label="Next video"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      <div
        className="relative w-full max-w-[min(90vw,calc(85vh*9/16))] aspect-[9/16] rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {isYouTube ? (
          <iframe
            key={index}
            src={`${t.src}${t.src!.includes("?") ? "&" : "?"}autoplay=1`}
            title={`${t.procedure} — patient video`}
            className="absolute inset-0 w-full h-full bg-black"
            frameBorder={0}
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : (
          <video
            key={index}
            src={t.src}
            poster={t.poster}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        )}
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-white/90 text-xs md:text-sm">
        {t.procedure} · {index + 1} / {items.length}
      </div>
    </div>,
    document.body,
  );
}

function VideoTestimonialsGallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 max-w-7xl mx-auto mt-10 md:mt-14">
        {VIDEO_TESTIMONIALS.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
          >
            <VideoTestimonialCard t={t} onOpen={() => setOpenIndex(i)} />
          </motion.div>
        ))}
      </div>
      <AnimatePresence>
        {openIndex !== null && (
          <VideoTestimonialLightbox
            items={VIDEO_TESTIMONIALS}
            index={openIndex}
            onClose={() => setOpenIndex(null)}
            onIndex={setOpenIndex}
          />
        )}
      </AnimatePresence>
    </>
  );
}
